import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import linkstate from 'linkstate';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { printViaIframe } from '../../lib/printHelper';
import { getAccountName } from '../../lib/accountMap';
import { otherPaymentsTotal } from '../../lib/saleReportTotals';
import { shiftDate, shiftDateTime } from '../../lib/dateStep';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import DyrejournalReportPrint from '../../components/dyrejournal/reportPrint';
import DyrejournalReportSalesAndPayments from '../../components/dyrejournal/reportSalesAndPayments';
import DyrejournalReportUserDetailPrint from '../../components/dyrejournal/reportUserDetailPrint';
import DyrejournalReportUserDetailProductsPrint from '../../components/dyrejournal/reportUserDetailProductsPrint';
import DyrejournalReportAllSalesPrint from '../../components/dyrejournal/reportAllSalesPrint';
import DyrejournalJournals from '../../components/dyrejournal/journals';
import MiniGraph from '../../components/lineGraph/mini';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

const USER_HEIGHT = 200;
const PRODUCT_HEIGHT = 150;
const REPORTLIST_HEIGHT = 350;
// localStorage key for the daily report's "Skjul kredittsalg" switch (per browser)
const HIDE_CREDIT_SALES_KEY = 'dyrejournalReportHideCreditSales';

const MONTH_NAMES = [
    'jan', 'feb', 'mar', 'apr', 'mai', 'jun',
    'jul', 'aug', 'sep', 'okt', 'nov', 'des',
];

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

function getReportDate(inputDate, reportType, onlyFromDate) {
    const reportDate = new Date(`${inputDate}T00:00:00`);
    // console.log({ inputDate, reportDate})
    // console.log({ reportType })
    const reportDay = reportDate.getDate();
    const reportMonth = reportDate.getMonth();
    const reportYear = reportDate.getFullYear();

    if (reportType === 'day') {
        return `${reportDay}.${MONTH_NAMES[reportMonth]} ${reportYear}`;
    } else if (reportType === 'z-report') {
        return `${reportDay}.${MONTH_NAMES[reportMonth]} ${reportYear}`;
    } else if (reportType === 'month') {
        return `${onlyFromDate ? reportDay : 1} - ${util.daysInMonth(reportMonth + 1, reportYear)}.${MONTH_NAMES[reportMonth]} ${reportYear}`;
    } else if (reportType === 'year') {
        return `1.jan - 31.des ${reportYear}`;
    }
    return '';//`${reportDay}.${MONTH_NAMES[reportMonth]} ${reportYear}`;
}


@withText(props => ({
    productTitle: <Text id='dyrejournal.product.search-title'>Search for product</Text>,
}))
@observer
class AllProducts extends Component {
	constructor(props) {
        super(props);
        const reportDateTimeStart = new Date();
        reportDateTimeStart.setHours(0, 0, 0, 0);
        this.state = {
            sections: fields.get('journal'),
            reportDate: util.isoDate(new Date(), false, false, true),
            reportDateTimeStart: util.isoDate(reportDateTimeStart, true, false),
            reportDateTimeEnd: util.isoDate(new Date(), true, false),
            showCashflowAnalysis: false,
            showCardBreakdown: false,
            showWriteoffs: false,
            expandedCashflowSections: {},
            accountsYearlyYear: new Date().getFullYear(),
            accountsYearlyStartMonth: 1,
            accountsYearlyEndMonth: 12,
            accountsReceivableCutoffDate: util.isoDate(new Date(), false, false, true),
            accountsReceivableStartDate: '',
            expandedAccountRows: {},
            rawDataProductSearch: '',
            activePaymentMethod: '',
            showCustomerNumber: false,
            // Per browser: hides the Kredittsalg columns and rows in the daily report (TICKET-01763)
            hideCreditSales: util.get(HIDE_CREDIT_SALES_KEY) === true,
        };
        this.searchTimer = null;
        this.productsContainer = null;
        this.usersContainer = null;
        this.extendedReportlist = null;
        this.reportRequestVersion = 0;
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { productStore, saleStore, saleReportStore, appState } = this.props.stores;
        // await productStore.load();
        // await saleStore.loadDailyReport();
        // await saleStore.loadMonthlyReport();
        const { view = {} } = appState;
        this.setState({ loading: true });
        this.resetCashflowAnalysis();
        this.resetCardBreakdown();

        // Increment and capture version in store
        const requestVersion = saleStore.incrementReportRequestVersion();

        let result;
        if (view.report === 'day') {
            result = await saleStore.loadDailyReport(this.state.reportDate, { requestVersion });
        } else if (view.report === 'periode') {
            const { reportDateTimeStart, reportDateTimeEnd } = this.state;
            result = await saleStore.loadPeriodeReport(reportDateTimeStart, reportDateTimeEnd, { requestVersion });
        } else if (view.report === 'z-report') {
            result = await saleStore.loadZReport('', { requestVersion });
        } else if (view.report === 'month') {
            result = await saleStore.loadMonthlyReport(new Date(), false, { requestVersion });
        } else if (view.report === 'year') {
            result = await saleStore.loadYearlyReport(new Date(), false, { requestVersion });
        } else if (view.report === 'accounts-yearly') {
            const { accountsYearlyYear, accountsYearlyStartMonth, accountsYearlyEndMonth } = this.state;
            result = await saleStore.loadYearlyAccountsReport(accountsYearlyYear || new Date().getFullYear(), '3', accountsYearlyStartMonth, accountsYearlyEndMonth, { requestVersion });
        } else if (view.report === 'accounts-receivable') {
            const { accountsReceivableCutoffDate, accountsReceivableStartDate } = this.state;
            result = await saleStore.loadAccountsReceivableReport(accountsReceivableCutoffDate, { requestVersion, startDate: accountsReceivableStartDate });
        }

        // If stale, don't continue with setup
        if (result?.stale) return;

        // Load trends for dashboard (doesn't block report loading)
        saleStore.loadSaleTrends(12);
        saleStore.loadVisitorTrends(12);

        this.setupSaleReport();
        this.setState({ loading: false });
        saleReportStore.load();
    }

    setupSaleReport = () => {
        const { appState, saleStore } = this.props.stores;
        const { aggSaleTotal, aggSaleTotalDiscount, aggSaleTotalNew, aggSalePersons, aggSalePersonsAccounts, aggSaleTotalAccounts, aggSaleTotalProducts, aggSaleWithJournals } = saleStore;
        const { view = {} } = appState;
        const totalForPeriode = aggSaleTotal?.price;
        const saleReport = {
            reportType: view.report,
            customerDepartment: saleStore.reportDepartment || '',
            ...(view.report === 'day' && saleStore.cashCarryForward ? {
                openingCash: saleStore.cashCarryForward.openingCash,
                cashCarryForwardReport: saleStore.cashCarryForward.cashCarryForwardReport,
                addedChange: saleStore.cashCarryForward.currentDayCash?.addedChange || 0,
                bankWithdrawal: saleStore.cashCarryForward.currentDayCash?.bankWithdrawal || 0,
            } : {}),
            amountCash:       saleStore.getPaymentMethodTotalNew('cash'), // Actual payment
            // amountCard includes both clinic-terminal ('card') and external-terminal ('cardExternal') payments
            // so the fixed reconciliation tables and their totals stay correct. The clinic-vs-external split
            // is shown separately in the "Fordeling per betalingsmetode" breakdown.
            amountCard:       saleStore.getPaymentMethodTotalNew('card') + saleStore.getPaymentMethodTotalNew('cardExternal'), // Actual payment
            amountCreditCard: saleStore.getPaymentMethodTotalNew('creditCard'), // Actual payment
            amountVipps:      saleStore.getPaymentMethodTotalNew('vipps'), // Actual payment
            amountAccount:    saleStore.getPaymentMethodTotalNew('account'), // Actual payment

            saleAmountCash:       saleStore.getPaymentMethodTotal('cash'), // Only sale with payment method cash
            saleAmountCard:       saleStore.getPaymentMethodTotal('card') + saleStore.getPaymentMethodTotal('cardExternal'), // Only sale with payment method card (incl. external terminal)
            saleAmountCreditCard: saleStore.getPaymentMethodTotal('creditCard'), // Only sale with payment method creditCard
            saleAmountVipps:      saleStore.getPaymentMethodTotal('vipps'), // Only sale with payment method vipps
            saleAmountAccount:    saleStore.getPaymentMethodTotal('account'), // Only sale with payment method account

            amountInvoice:    saleStore.getPaymentMethodTotal('invoice'), // Only sale with payment method invoice
            amountCreditNote: saleStore.getPaymentMethodTotal('creditNote'), // Only sale with payment method creditNote
            amountPartialCreditNote: saleStore.getPaymentMethodTotalNew('creditNote'), // Actual payment as creditNote
            amountPartial:    saleStore.getPaymentMethodTotal('partial'), // Only sale with payment method partial
            amountSaleCredited: saleStore.getPaymentMethodTotal('saleCredited'), // Only sale with payment method saleCredited
            amountInvoicePaid: saleStore.getPaymentMethodTotalNew('invoice'), // Actual payments registered on invoices
            amountOtherPaid: otherPaymentsTotal(saleStore.aggSaleTotalPaymentMethodsNew), // All other actual payments (insurance, roundoff, refunds, ...)
            saleAmountConvene: saleStore.getPaymentMethodTotal('convene'), // Only sale with payment method convene (set by the Convene callback)
            amountConvene: saleStore.getPaymentMethodTotalNew('convene'), // Actual payments reported by Convene (also inside amountOtherPaid)
            // amountUnpaid:     saleStore.getPaymentMethodTotal('unpaid'),
            amountTotal: totalForPeriode,
            amountDiscount: aggSaleTotalDiscount?.price,
            // amountTotal: (view.report === 'day' ?
            //     (
            //         saleStore.getPaymentMethodTotalNew('cash') +
            //         saleStore.getPaymentMethodTotalNew('card') +
            //         saleStore.getPaymentMethodTotalNew('vipps') +
            //         saleStore.getPaymentMethodTotalNew('invoice') +
            //         saleStore.getPaymentMethodTotalNew('account')
            //     ) :
            //     (
            //         saleStore.getPaymentMethodTotalNew('cash') +
            //         saleStore.getPaymentMethodTotalNew('card') +
            //         saleStore.getPaymentMethodTotalNew('vipps') +
            //         saleStore.getPaymentMethodTotal('invoice') +
            //         saleStore.getPaymentMethodTotalNew('account') +
            //         saleStore.getPaymentMethodTotalNew('creditNote')
            //     )
            // ),
            // Real VAT summed per line at its own rate by the backend (0 % lines carry
            // none), instead of assuming 25 % on the whole turnover. TICKET-01539.
            amountTax: aggSaleTotal?.vatAmount || 0,
            // amountTax: (view.report === 'day' ?
            //     (
            //         saleStore.getPaymentMethodTotalNew('cash') +
            //         saleStore.getPaymentMethodTotalNew('card') +
            //         saleStore.getPaymentMethodTotalNew('vipps') +
            //         saleStore.getPaymentMethodTotalNew('invoice') +
            //         saleStore.getPaymentMethodTotalNew('account')
            //     ) / 1.25 * 0.25 :
            //     (
            //         saleStore.getPaymentMethodTotalNew('cash') +
            //         saleStore.getPaymentMethodTotalNew('card') +
            //         saleStore.getPaymentMethodTotalNew('vipps') +
            //         saleStore.getPaymentMethodTotalNew('invoice') +
            //         saleStore.getPaymentMethodTotalNew('account') +
            //         saleStore.getPaymentMethodTotalNew('creditNote')
            //     ) / 1.25 * 0.25
            // ),
            salesPersons: aggSalePersons,
            salePersonsAccounts: aggSalePersonsAccounts,
            saleAccounts: aggSaleTotalAccounts,
        };
        this.setState({ saleReport });
    }

    // onClickChooseProduct = e => {
    //     e.preventDefault();
    //     e.stopPropagation();
    //     const { id } = e.target.closest('tr').dataset;
    //     const { drawerLevel = 1 } = this.props;
	// 	const { appState, saleStore, productStore } = this.props.stores;
    //     const { newSale } = saleStore;
    //     const { products = [] } = newSale;
    //     const { products: allProducts } = productStore;
    //     const selectedItem = allProducts.find(p => p.id === parseInt(id, 10));
    //     if (selectedItem) {
    //         saleStore.updateObjectKeyValue('newSale', 'products', [...products, selectedItem]);
    //         appState.toggleDrawerRight(false, drawerLevel);
    //     }
    // }

    // onClickEditProduct = e => {
    //     e.preventDefault();
    //     e.stopPropagation();
    //     const { id } = e.target.closest('tr').dataset;
    //     const { drawerLevel = 1 } = this.props;
	// 	const { appState, saleStore } = this.props.stores;
	// 	const { drawerHeightMedium } = appState;
	// 	appState.openDrawer('editProduct', {
	// 		height: drawerHeightMedium,
    //         hideAdmin: true,
    //         id,
    //         callback: product => {
    //             appState.toggleDrawer(false, drawerLevel + 1);
    //             if (product) {
    //                 const { newSale, productResults } = saleStore;
    //                 const { products } = newSale;
    //                 saleStore.updateObjectKeyValue('newSale', 'products', [...products, { ...product, qty: 1 }]);
    //                 setTimeout(() => {
    //                     this.loadAll(this.props, false);
    //                 }, 1000);
    //             }
    //         },
	// 	}, drawerLevel + 1);
    // }

    // searchProductCallback = selectedItem => {
    //     const { appState, saleStore, productStore } = this.props.stores;
    //     const { drawerLevel = 1 } = this.props;
    //     const { newSale } = saleStore;
    //     const { products = [] } = newSale;
    //     saleStore.updateObjectKeyValue('newSale', 'products', [...products, selectedItem]);
    //     appState.toggleDrawerRight(false, drawerLevel);
    // }

    // onInput handler for a field inside this.state.saleReport (used by the shared sales/payments block).
    inputFor = field => linkstate(this, `saleReport.${field}`);

    onClickPrintReport = async e => {
        const { userStore, saleReportStore } = this.props.stores;
        const { id } = e.target.closest('button').dataset;
        await saleReportStore.load(id);

        // console.log('onClickPrintSale', e);
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalReportPrint stores={this.props.stores} id={id} />
        </IntlProvider>;

        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);
    }

    onClickSaveReport = async () => {
        const { saleReportStore, saleStore, userStore } = this.props.stores;
        const { saleReport, reportDate, reportDateTimeStart, reportDateTimeEnd } = this.state;
        const carryEnabled = Number(userStore.customer?.featureFlags?.cashCarryForward) === 1;
        if (carryEnabled && saleReport.reportType === 'day'
            && (saleStore.cashCarryForward?.reportDate !== reportDate
                || saleStore.cashCarryForward?.customerDepartment !== (saleStore.reportDepartment || ''))) {
            this.setState({ saveReportError: true });
            return;
        }
        this.setState({ saveReportInserting: true, saveReportError: false });
        try {
            const response = await saleReportStore.insert({
                ...saleReport,
                reportDate: util.isoDate(reportDate),
                reportDateTimeStart: util.isoDate(reportDateTimeStart, true),
                reportDateTimeEnd: util.isoDate(reportDateTimeEnd, true),
                calledFrom: 'reports.onClickSaveReport',
            });
            if (response?.status !== 201) {
                this.setState({ saveReportError: true });
                return;
            }
            const savedReport = response?.data?.salereport || response?.data?.saleReport;
            saleReportStore.updateKeyValue('saleReport', savedReport);
            await saleReportStore.load();
        } catch (error) {
            this.setState({ saveReportError: true });
        } finally {
            this.setState({ saveReportInserting: false });
        }
    }

    onClickUpdateList = async e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ saveReportUpdating: true });
        const { saleReportStore } = this.props.stores;
        await saleReportStore.load();
        this.setState({ saveReportUpdating: false });
    }

    onClickDeleteSaleReport = async e => {
        e.preventDefault();
        e.stopPropagation();
        const dialogAction = beginDialogAction(this, 'onClickDeleteSaleReport');
        if (!dialogAction) return false;
        try {
            const dialogTargetData = { ...e.target.closest('button').dataset };



            if (!((await appConfirm('Are you sure you want to delete this saleReport?')) && dialogAction.isCurrent())) {
                return;
            }

            const { id } = dialogTargetData;
            const { saleReportStore } = this.props.stores;
            await saleReportStore.delete(parseInt(id, 10));
            setTimeout(() => {
                saleReportStore.load();
            }, 1000);
        } finally {
            dialogAction.finish();
        }
    }

    onClickShowCashflowAnalysis = async () => {
        const { saleStore, appState } = this.props.stores;
        const { view } = appState;
        const { reportDate, reportDateTimeStart, reportDateTimeEnd } = this.state;

        let startDate, endDate;
        if (view.report === 'day' || view.report === 'periode') {
            const { start, end } = getReportDate(reportDate, view.report === 'day' ? 'day' : 'periode', false);
            startDate = util.isoDate(start, false, false, true);
            endDate = util.isoDate(end, false, false, true);
        } else if (view.report === 'month') {
            const d = new Date(`${reportDate}T00:00:00`);
            startDate = util.isoDate(new Date(d.getFullYear(), d.getMonth(), 1), false, false, true);
            endDate = util.isoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0), false, false, true);
        } else if (view.report === 'year') {
            const d = new Date(`${reportDate}T00:00:00`);
            startDate = util.isoDate(new Date(d.getFullYear(), 0, 1), false, false, true);
            endDate = util.isoDate(new Date(d.getFullYear(), 11, 31), false, false, true);
        }

        this.setState({ showCashflowAnalysis: true });
        await saleStore.loadCashflowAnalysis(startDate, endDate);
    }

    onClickCloseCashflowAnalysis = () => {
        this.setState({ showCashflowAnalysis: false });
    }

    resetCashflowAnalysis = () => {
        const { saleStore } = this.props.stores;
        this.setState({ showCashflowAnalysis: false });
        saleStore.updateKeyValue('cashflowAnalysis', null);
    }

    toggleCashflowSection = (sectionKey) => {
        this.setState(prevState => ({
            expandedCashflowSections: {
                ...prevState.expandedCashflowSections,
                [sectionKey]: !prevState.expandedCashflowSections[sectionKey],
            },
        }));
    }

    onClickShowCardBreakdown = async () => {
        const { saleStore, appState } = this.props.stores;
        const { view } = appState;
        const { reportDate } = this.state;

        let startDate, endDate;
        if (view.report === 'day' || view.report === 'periode') {
            const { start, end } = getReportDate(reportDate, view.report === 'day' ? 'day' : 'periode', false);
            startDate = util.isoDate(start, false, false, true);
            endDate = util.isoDate(end, false, false, true);
        } else if (view.report === 'month') {
            const d = new Date(`${reportDate}T00:00:00`);
            startDate = util.isoDate(new Date(d.getFullYear(), d.getMonth(), 1), false, false, true);
            endDate = util.isoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0), false, false, true);
        } else if (view.report === 'year') {
            const d = new Date(`${reportDate}T00:00:00`);
            startDate = util.isoDate(new Date(d.getFullYear(), 0, 1), false, false, true);
            endDate = util.isoDate(new Date(d.getFullYear(), 11, 31), false, false, true);
        }

        this.setState({ showCardBreakdown: true });
        await saleStore.loadCardBreakdown(startDate, endDate);
    }

    onClickCloseCardBreakdown = () => {
        this.setState({ showCardBreakdown: false });
    }

    resetCardBreakdown = () => {
        const { saleStore } = this.props.stores;
        this.setState({ showCardBreakdown: false });
        saleStore.updateKeyValue('cardBreakdown', null);
    }

    onClickShowWriteoffs = async () => {
        const { saleStore, appState } = this.props.stores;
        const { reportDate } = this.state;
        const { view = {} } = appState;

        let startDate, endDate;
        const d = new Date(reportDate);

        if (view.report === 'day') {
            startDate = util.isoDate(d, false, false, true);
            endDate = util.isoDate(d, false, false, true);
        } else if (view.report === 'month') {
            startDate = util.isoDate(new Date(d.getFullYear(), d.getMonth(), 1), false, false, true);
            endDate = util.isoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0), false, false, true);
        } else if (view.report === 'year') {
            startDate = util.isoDate(new Date(d.getFullYear(), 0, 1), false, false, true);
            endDate = util.isoDate(new Date(d.getFullYear(), 11, 31), false, false, true);
        }

        this.setState({ showWriteoffs: true });
        await saleStore.loadWriteoffs(startDate, endDate);
    }

    onClickCloseWriteoffs = () => {
        this.setState({ showWriteoffs: false });
    }

    resetWriteoffs = () => {
        const { saleStore } = this.props.stores;
        this.setState({ showWriteoffs: false });
        saleStore.updateKeyValue('writeoffs', []);
        saleStore.updateKeyValue('writeoffsSummary', {});
    }

    changeReportDate = async e => {
        const { name } = e.target.dataset;
        const { value } = e.target;
        this.setState({ [name]: value, loading: true });
        clearTimeout(this.setReportDateTimer);
        this.setReportDateTimer = setTimeout(() => {
            this.doChangeReportDate(value);
        }, 1000);
    }

    doChangeReportDate = async (value) => {
        this.setState({ loading: true });
        this.resetCashflowAnalysis();
        this.resetCardBreakdown();
        const { onlyFromDate } = this.state;
        const { saleStore, appState } = this.props.stores;
        const { view = {} } = appState;

        // Increment and capture version in store
        const requestVersion = saleStore.incrementReportRequestVersion();

        let result;
        if (view.report === 'day') {
            result = await saleStore.loadDailyReport(value, { requestVersion });
        } else if (view.report === 'z-report') {
            result = await saleStore.loadZReport(value, { requestVersion });
        } else if (view.report === 'month') {
            result = await saleStore.loadMonthlyReport(value, onlyFromDate, { requestVersion });
        } else if (view.report === 'year') {
            result = await saleStore.loadYearlyReport(value, onlyFromDate, { requestVersion });
        }

        // If stale, don't continue with setup
        if (result?.stale) return;

        this.setupSaleReport();
        this.setState({ loading: false });
    }

    changeReportDateTime = async e => {
        const { name } = e.target.dataset;
        const { value } = e.target;
        const { appState } = this.props.stores;
        const { view = {} } = appState;
        clearTimeout(this.setReportDateTimeTimer);
        this.setState({ [name]: value }, () => {
            this.setReportDateTimeTimer = setTimeout(() => {
                this.doChangeReportDateTime();
            }, 500);
        });
    }

    onClickStepReportDate = e => {
        const direction = parseInt(e.currentTarget.dataset.direction, 10);
        const { appState } = this.props.stores;
        const { view = {} } = appState;

        if (view.report === 'periode') {
            const { reportDateTimeStart, reportDateTimeEnd } = this.state;
            clearTimeout(this.setReportDateTimeTimer);
            this.setState({
                reportDateTimeStart: shiftDateTime(reportDateTimeStart, direction),
                reportDateTimeEnd: shiftDateTime(reportDateTimeEnd, direction),
            }, () => {
                this.setReportDateTimeTimer = setTimeout(() => {
                    this.doChangeReportDateTime();
                }, 500);
            });
            return;
        }

        let unit = 'day';
        if (view.report === 'month') {
            unit = 'month';
        } else if (view.report === 'year') {
            unit = 'year';
        }
        const { reportDate } = this.state;
        const value = shiftDate(reportDate, unit, direction);
        if (!value || value === reportDate) return;
        this.setState({ reportDate: value, loading: true });
        clearTimeout(this.setReportDateTimer);
        this.setReportDateTimer = setTimeout(() => {
            this.doChangeReportDate(value);
        }, 1000);
    }

    doChangeReportDateTime = async () => {
        this.setState({ loading: true });
        this.resetCashflowAnalysis();
        this.resetCardBreakdown();
        const { saleStore, appState } = this.props.stores;
        const { view = {} } = appState;
        const { reportDateTimeStart, reportDateTimeEnd } = this.state;

        // Increment and capture version in store
        const requestVersion = saleStore.incrementReportRequestVersion();

        let result;
        if (view.report === 'periode') {
            result = await saleStore.loadPeriodeReport(reportDateTimeStart, reportDateTimeEnd, { requestVersion });
        }

        // If stale, don't continue with setup
        if (result?.stale) return;

        this.setupSaleReport();
        this.setState({ loading: false });
    }

    toggleOnlyFromDate = async e => {
        const { checked: onlyFromDate } = e.target;
        this.setState({ onlyFromDate, loading: true });
        this.resetCashflowAnalysis();
        this.resetCardBreakdown();
        const { saleStore, appState } = this.props.stores;
        const { view = {} } = appState;
        const { reportDate } = this.state;

        // Increment and capture version in store
        const requestVersion = saleStore.incrementReportRequestVersion();

        let result;
        if (view.report === 'day') {
            result = await saleStore.loadDailyReport(reportDate, { requestVersion });
        } else if (view.report === 'month') {
            result = await saleStore.loadMonthlyReport(reportDate, onlyFromDate, { requestVersion });
        } else if (view.report === 'year') {
            result = await saleStore.loadYearlyReport(reportDate, onlyFromDate, { requestVersion });
        }

        // If stale, don't continue with setup
        if (result?.stale) return;

        this.setupSaleReport();
        this.setState({ loading: false });
    }

    toggleShowCustomerNumber = e => {
        const { checked: showCustomerNumber } = e.target;
        this.setState({ showCustomerNumber });
    }

    toggleHideCreditSales = e => {
        const { checked: hideCreditSales } = e.target;
        this.setState({ hideCreditSales });
        try {
            util.set(HIDE_CREDIT_SALES_KEY, hideCreditSales);
        } catch (err) {
            // ignore localStorage errors (quota, private mode); the choice still applies for this page view
        }
    }

    changeDepartment = async e => {
        const { onlyFromDate } = this.state;
        const { value } = e.target;
        const departmentId = value;
        const { saleStore, appState } = this.props.stores;
        const { view = {} } = appState;
        this.setState({ loading: true });
        this.resetCashflowAnalysis();
        this.resetCardBreakdown();

        // Increment and capture version in store
        const requestVersion = saleStore.incrementReportRequestVersion();

        await saleStore.updateKeyValue('reportDepartment', departmentId);
        const { reportDate } = this.state;

        let result;
        if (view.report === 'day') {
            result = await saleStore.loadDailyReport(reportDate, { requestVersion });
        } else if (view.report === 'month') {
            result = await saleStore.loadMonthlyReport(reportDate, onlyFromDate, { requestVersion });
        } else if (view.report === 'year') {
            result = await saleStore.loadYearlyReport(reportDate, onlyFromDate, { requestVersion });
        } else if (view.report === 'periode') {
            const { reportDateTimeStart, reportDateTimeEnd } = this.state;
            result = await saleStore.loadPeriodeReport(reportDateTimeStart, reportDateTimeEnd, { requestVersion });
        } else if (view.report === 'z-report') {
            result = await saleStore.loadZReport(reportDate, { requestVersion });
        } else if (view.report === 'accounts-yearly') {
            const { accountsYearlyYear, accountsYearlyStartMonth, accountsYearlyEndMonth } = this.state;
            result = await saleStore.loadYearlyAccountsReport(accountsYearlyYear || new Date().getFullYear(), '3', accountsYearlyStartMonth, accountsYearlyEndMonth, { requestVersion });
        } else if (view.report === 'accounts-receivable') {
            const { accountsReceivableCutoffDate, accountsReceivableStartDate } = this.state;
            result = await saleStore.loadAccountsReceivableReport(accountsReceivableCutoffDate, { requestVersion, startDate: accountsReceivableStartDate });
        }

        // If stale, don't continue with setup
        if (result?.stale) return;

        this.setupSaleReport();
        this.setState({ loading: false });
    }

    selectView = (e) => {
        const { appState } = this.props.stores;
        const { type } = e.target.closest('button').dataset;
        appState.setViewKey('report', type);
        this.loadAll();
    }

    changeAccountsYearlyYear = async e => {
        const { value } = e.target;
        const { accountsYearlyStartMonth, accountsYearlyEndMonth } = this.state;
        this.setState({ accountsYearlyYear: parseInt(value, 10), loading: true });
        const { saleStore } = this.props.stores;

        // Increment and capture version in store
        const requestVersion = saleStore.incrementReportRequestVersion();

        const result = await saleStore.loadYearlyAccountsReport(parseInt(value, 10), '3', accountsYearlyStartMonth, accountsYearlyEndMonth, { requestVersion });

        // If stale, don't continue
        if (result?.stale) return;

        this.setState({ loading: false });
    }

    changeAccountsYearlyStartMonth = async e => {
        const { value } = e.target;
        const { accountsYearlyYear, accountsYearlyEndMonth } = this.state;
        this.setState({ accountsYearlyStartMonth: parseInt(value, 10), loading: true });
        const { saleStore } = this.props.stores;

        // Increment and capture version in store
        const requestVersion = saleStore.incrementReportRequestVersion();

        const result = await saleStore.loadYearlyAccountsReport(accountsYearlyYear, '3', parseInt(value, 10), accountsYearlyEndMonth, { requestVersion });

        // If stale, don't continue
        if (result?.stale) return;

        this.setState({ loading: false });
    }

    changeAccountsYearlyEndMonth = async e => {
        const { value } = e.target;
        const { accountsYearlyYear, accountsYearlyStartMonth } = this.state;
        this.setState({ accountsYearlyEndMonth: parseInt(value, 10), loading: true });
        const { saleStore } = this.props.stores;

        // Increment and capture version in store
        const requestVersion = saleStore.incrementReportRequestVersion();

        const result = await saleStore.loadYearlyAccountsReport(accountsYearlyYear, '3', accountsYearlyStartMonth, parseInt(value, 10), { requestVersion });

        // If stale, don't continue
        if (result?.stale) return;

        this.setState({ loading: false });
    }

    changeAccountsReceivableCutoffDate = e => {
        const { value } = e.target;
        this.setState({ accountsReceivableCutoffDate: value });
        clearTimeout(this.setAccountsReceivableDateTimer);
        this.setAccountsReceivableDateTimer = setTimeout(() => {
            this.doReloadAccountsReceivable();
        }, 1000);
    }

    changeAccountsReceivableStartDate = e => {
        const { value } = e.target;
        this.setState({ accountsReceivableStartDate: value });
        clearTimeout(this.setAccountsReceivableDateTimer);
        this.setAccountsReceivableDateTimer = setTimeout(() => {
            this.doReloadAccountsReceivable();
        }, 1000);
    }

    doReloadAccountsReceivable = async () => {
        this.setState({ loading: true });
        const { saleStore } = this.props.stores;
        const { accountsReceivableCutoffDate, accountsReceivableStartDate } = this.state;

        // Increment and capture version in store
        const requestVersion = saleStore.incrementReportRequestVersion();

        const result = await saleStore.loadAccountsReceivableReport(accountsReceivableCutoffDate, { requestVersion, startDate: accountsReceivableStartDate });

        // If stale, don't continue
        if (result?.stale) return;

        this.setState({ loading: false });
    }

    downloadYearlyAccountsCSV = () => {
        const { saleStore, userStore } = this.props.stores;
        const { yearlyAccountsReport } = saleStore;
        if (!yearlyAccountsReport) return;

        const { accountRows, monthNames, grandTotal, monthlyTotals } = yearlyAccountsReport;
        const BOM = '\uFEFF';
        let csv = BOM;

        // Header row
        csv += `Konto;Kontonavn;${monthNames.join(';')};Sum\n`;

        // Data rows
        accountRows.forEach(row => {
            const accountName = getAccountName(row.account, userStore.customer) || row.account || 'Ingen konto';
            const monthValues = row.months.map(v => v.toFixed(2).replace('.', ',')).join(';');
            csv += `${row.account};${accountName};${monthValues};${row.total.toFixed(2).replace('.', ',')}\n`;
        });

        // Total row
        const totalMonths = monthlyTotals.map(v => v.toFixed(2).replace('.', ',')).join(';');
        csv += `TOTALT;;${totalMonths};${grandTotal.toFixed(2).replace('.', ',')}\n`;

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `kontorapport-${yearlyAccountsReport.year}.csv`;
        link.click();
    }

    downloadAccountsReceivableCSV = () => {
        const { saleStore } = this.props.stores;
        const { accountsReceivableReport } = saleStore;
        if (!accountsReceivableReport) return;

        const { unpaidInvoices, grandTotal, cutoffDate } = accountsReceivableReport;
        const BOM = '\uFEFF';
        let csv = BOM;

        // Header row
        csv += `Fakturanr;Kunde;Kundenr;Fakturadato;Totalbeløp;Innbetalt;Beløp;0-30 dager;31-60 dager;61-90 dager;Over 90 dager\n`;

        // Data rows
        unpaidInvoices.forEach(inv => {
            const saleDate = inv.saleDate ? util.isoDate(inv.saleDate, false, false, true) : '';
            csv += `${inv.saleNumber};${inv.visitorName};${inv.visitorId || ''};${saleDate};${(inv.saleTotal || 0).toFixed(2).replace('.', ',')};${(inv.totalPaid || 0).toFixed(2).replace('.', ',')};${inv.unpaidAmount.toFixed(2).replace('.', ',')};`;
            csv += `${inv.bucket === '0-30' ? inv.unpaidAmount.toFixed(2).replace('.', ',') : ''};`;
            csv += `${inv.bucket === '31-60' ? inv.unpaidAmount.toFixed(2).replace('.', ',') : ''};`;
            csv += `${inv.bucket === '61-90' ? inv.unpaidAmount.toFixed(2).replace('.', ',') : ''};`;
            csv += `${inv.bucket === '90+' ? inv.unpaidAmount.toFixed(2).replace('.', ',') : ''}\n`;
        });

        // Total row
        const buckets = accountsReceivableReport.agingBuckets;
        csv += `TOTALT;;;;;;${grandTotal.toFixed(2).replace('.', ',')};`;
        csv += `${buckets['0-30'].total.toFixed(2).replace('.', ',')};`;
        csv += `${buckets['31-60'].total.toFixed(2).replace('.', ',')};`;
        csv += `${buckets['61-90'].total.toFixed(2).replace('.', ',')};`;
        csv += `${buckets['90+'].total.toFixed(2).replace('.', ',')}\n`;

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `utestaaende-${cutoffDate}.csv`;
        link.click();
    }

    printYearlyAccountsReport = () => {
        const printSection = document.querySelector('.print-section');
        if (printSection) {
            const wrapper = document.createElement('div');
            wrapper.className = 'receipt';
            wrapper.innerHTML = printSection.outerHTML;
            printViaIframe(wrapper.outerHTML);
        }
    }

    printAccountsReceivableReport = () => {
        const printSection = document.querySelector('.print-section');
        if (printSection) {
            const wrapper = document.createElement('div');
            wrapper.className = 'receipt';
            wrapper.innerHTML = printSection.outerHTML;
            printViaIframe(wrapper.outerHTML);
        }
    }

    onClickUserDetail = async e => {
        const { userStore, saleStore } = this.props.stores;

        // data-date={reportDate}
        // data-type={view.report}
        // data-onlyfromdate={onlyFromDate}
        const { id, date, type, onlyfromdate, datefrom = '', dateto = '' } = e.target.closest('button').dataset;
        await saleStore.loadReportUserDetails(id, date, type, onlyfromdate, datefrom, dateto);

        // console.log('onClickPrintSale', e);
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalReportUserDetailPrint stores={this.props.stores} id={id} date={date} type={type} onlyfromdate={onlyfromdate} />
        </IntlProvider>;

        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);
    }

    onClickUserDetailProducts = async e => {
        const { userStore, saleStore } = this.props.stores;

        // data-date={reportDate}
        // data-type={view.report}
        // data-onlyfromdate={onlyFromDate}
        const { id, date, type, onlyfromdate, datefrom = '', dateto = '' } = e.target.closest('button').dataset;
        await saleStore.loadReportUserDetailsProducts(id, date, type, onlyfromdate, datefrom, dateto);

        // console.log('onClickPrintSale', e);
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalReportUserDetailProductsPrint stores={this.props.stores} id={id} date={date} type={type} onlyfromdate={onlyfromdate} />
        </IntlProvider>;

        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);
    }

    onClickAllSales = async e => {
        const { userStore, saleStore } = this.props.stores;

        const { date, type, onlyfromdate, datefrom = '', dateto = '', paymentmethod = '' } = e.target.closest('button').dataset;
        const { showCustomerNumber } = this.state;
        await saleStore.loadReportAllSales(date, type, onlyfromdate, datefrom, dateto, paymentmethod);

        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalReportAllSalesPrint stores={this.props.stores} date={date} type={type} onlyfromdate={onlyfromdate} showCustomerNumber={showCustomerNumber} />
        </IntlProvider>;

        const tempDiv = document.createElement('div');
        tempDiv.className = 'receipt';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        printViaIframe(htmlContent);
    }

    toggleExtendedProduct = e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ extendedProduct: !this.state.extendedProduct });
    }

    toggleExtendedUser = e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ extendedUser: !this.state.extendedUser });
    }

    toggleExtendedPaymentUser = e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ extendedPaymentUser: !this.state.extendedPaymentUser });
    }

    toggleExtendedDepartment = e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ extendedDepartment: !this.state.extendedDepartment });
    }

    toggleExtendedReportlist = e => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ extendedReportlist: !this.state.extendedReportlist });
    }

    toggleAccountRow = (accountKey) => {
        this.setState(prevState => ({
            expandedAccountRows: {
                ...prevState.expandedAccountRows,
                [accountKey]: !prevState.expandedAccountRows[accountKey],
            },
        }));
    }

    // Sale links inside this report drawer: the route change alone loads the sale
    // BEHIND the still-open drawer, so the user only sees the report jump to the
    // top (TICKET-01840). Close the drawer first, like the journals drawer does.
    onClickGotoSale = (e, saleId) => {
        e.preventDefault();
        e.stopPropagation();
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawerRight(false, drawerLevel);
        route(`/dyrejournal-sale/${saleId}`);
    }

    checkAllContainerHeights = () => {
        if (this.productsContainer) {
            const { height } = this.productsContainer.getBoundingClientRect();
            // console.log('height', height);
            if (height > PRODUCT_HEIGHT) {
                this.setState({ extendedProduct: false });
            }
        }
        if (this.usersContainer) {
            const { height } = this.usersContainer.getBoundingClientRect();
            // console.log('height', height);
            if (height > USER_HEIGHT) {
                this.setState({ extendedUser: false });
            }
        }
        if (this.extendedReportlist) {
            const { height } = this.extendedReportlist.getBoundingClientRect();
            // console.log('height', height);
            if (height > REPORTLIST_HEIGHT) {
                this.setState({ extendedReportlist: false });
            }
        }
    }

    componentDidMount() {
        this.loadAll();
        this.checkAllContainerHeights();
    }

    render() {
        const {
            reportDate,
            reportDateTimeStart,
            reportDateTimeEnd,
            saleReport = {},
            saveReportInserting,
            saveReportUpdating,
            onlyFromDate,
            extendedProduct,
            extendedUser,
            extendedPaymentUser,
            extendedDepartment,
            extendedReportlist,
            loading,
            showCashflowAnalysis,
            showCardBreakdown,
            showWriteoffs,
            expandedCashflowSections,
            accountsYearlyYear,
            accountsYearlyStartMonth,
            accountsYearlyEndMonth,
            accountsReceivableCutoffDate,
            accountsReceivableStartDate,
            expandedAccountRows,
            rawDataProductSearch,
            activePaymentMethod,
            showCustomerNumber,
            hideCreditSales,
        } = this.state;
        const {
            productTitle,
        } = this.props;
        const { userStore, saleStore, saleReportStore, appState } = this.props.stores;
        const { user, isAdmin, isCustomerAdmin, customer } = userStore;

        // Only allow access for customer admins
        if (!isCustomerAdmin) {
            return (
                <div class='w-100 d-flex justify-content-center p-5'>
                    <div class='alert alert-warning' role='alert'>
                        <i class='fa-duotone fa-lock me-2' />
                        <Text id='dyrejournal.reports.access-denied'>Du har ikke tilgang til rapporter. Kontakt administrator.</Text>
                    </div>
                </div>
            );
        }

        const departments = customer?.departments || [];
        const { featureFlags = {} } = customer || {};
        const showCreditCard = featureFlags.creditCard > 0;
        const showConvene = featureFlags.convenePayment > 0;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { view = {} } = appState;

        const noViewSelected = !view.report;
        const DATE_STEP_LABELS = { day: 'dag', periode: 'dag', month: 'måned', year: 'år' };
        const dateStepLabel = DATE_STEP_LABELS[view.report];
        const showDateStep = !!dateStepLabel;

        const {
            totalNumberOfSales,
            aggSaleTotal,
            aggSaleTotalCollectiveInvoice,
            aggSaleTotalDiscount,
            aggSalesWithDiscount,
            aggSaleTotalNew,
            aggSaleTotalPaymentMethods,
            aggSaleTotalTax,
            aggSalePersons,
            aggSalePaymentsByUser,
            aggSaleDepartments,
            aggSalePersonsAccounts,
            aggSaleTotalAccounts,
            aggSaleTotalProducts,
            aggSaleWithJournals,
            aggSaleNewVisitors,
            aggSaleActiveVisitors,
            reportStatus,
            reportDepartment,
            journalCount,
            journalWithoutCount,
            saleTrends,
            visitorTrends,
            cashflowAnalysis,
            cashflowAnalysisLoading,
            cardBreakdown,
            cardBreakdownLoading,
            writeoffs,
            writeoffsSummary,
            writeoffsLoading,
            yearlyAccountsReport,
            yearlyAccountsReportLoading,
            accountsReceivableReport,
            accountsReceivableReportLoading,
        } = saleStore;

        const { saleReports } = saleReportStore;

        let totalForAccounts = 0;
        let totalForAccountsIncVat = 0;
        if (aggSaleTotalAccounts) {
            totalForAccounts = aggSaleTotalAccounts.map(account => account.price).reduce((a, b) => a + b, 0);
            // Gross per account comes from the backend at each line's own VAT rate.
            // Only a backend without that field falls back to the old flat 25 %. TICKET-01539.
            totalForAccountsIncVat = aggSaleTotalAccounts
                .map(account => (account.priceIncVat ?? (account.price || 0) * 1.25))
                .reduce((a, b) => a + b, 0);
        }

        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();

        return (
            <div class='w-100 d-flex justify-content-center position-relative'>
                {loading && <>
                    <div class='position-absolute w-100 h-100 d-flex flex-column justify-content-start align-items-center pt-5' style='z-index: 100000; background: rgba(255,255,255,0.7); top: 0; left: 0;'>
                        <div class='loading-indicator'>
                            <i class='fa-duotone fa-spinner-third fa-spin fa-3x' />
                        </div>
                    </div>
                </>}
                <div class='w-100 h-100' style='padding-top: 0px; padding-bottom: 100px;'>

                    {reportStatus ? <>
                        <div class='alert alert-danger' role='alert'>
                            <Text id='dyrejournal.report.error'>Error: {reportStatus}</Text>
                        </div>
                    </> : <></>}

                    <div class='w-100 d-flex flex-column'>
                        <div class='w-100 d-flex flex-column mt-1'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <div class='d-flex flex-nowrap'>
                                    <button type='button' class={`btn btn-${view.report === 'day' ? 'primary' : 'outline-secondary'} rounded-pill-left`} onClick={this.selectView} data-type={'day'}>
                                        <Text id='dyrejournal.report.daily'>Daily</Text>
                                    </button>
                                    {/* <button type='button' class={`btn btn-${view.report === 'z-report' ? 'primary' : 'outline-secondary'} rounded-none`} onClick={this.selectView} data-type={'z-report'}>
                                        <Text id='dyrejournal.report.z-report'>Z-report</Text>
                                    </button> */}
                                    <button type='button' class={`btn btn-${view.report === 'periode' ? 'primary' : 'outline-secondary'} rounded-none`} onClick={this.selectView} data-type={'periode'}>
                                        <Text id='dyrejournal.report.periode'>Periode</Text>
                                    </button>
                                    <button type='button' class={`btn btn-${view.report === 'month' ? 'primary' : 'outline-secondary'} rounded-none`} onClick={this.selectView} data-type={'month'}>
                                        <Text id='dyrejournal.report.monthly'>Monthly</Text>
                                    </button>
                                    <button type='button' class={`btn btn-${view.report === 'year' ? 'primary' : 'outline-secondary'} rounded-pill-right`} onClick={this.selectView} data-type={'year'}>
                                        <Text id='dyrejournal.report.yearly'>Yearly</Text>
                                    </button>
                                    <span class='mx-2'></span>
                                    <button type='button' class={`btn btn-${view.report === 'accounts-yearly' ? 'primary' : 'outline-secondary'} rounded-pill-left`} onClick={this.selectView} data-type={'accounts-yearly'}>
                                        <Text id='dyrejournal.report.accounts-yearly'>Accounts Yearly</Text>
                                    </button>
                                    <button type='button' class={`btn btn-${view.report === 'accounts-receivable' ? 'primary' : 'outline-secondary'} rounded-pill-right`} onClick={this.selectView} data-type={'accounts-receivable'}>
                                        <Text id='dyrejournal.report.accounts-receivable'>Accounts Receivable</Text>
                                    </button>
                                </div>

                                <div class='d-flex flex-column'>
                                    <select class='form-select rounded-pill' onChange={this.changeDepartment} value={reportDepartment || ''}>
                                        <option value=''>-- <Text id='dyrejournal.report.select-department'>Select department</Text> --</option>
                                        {departments.map(department => (
                                            <option key={department.id} value={department.id}>{department.id}: {department.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div class='d-flex flex-column'>
                                    <div class='d-flex flex-row'>
                                        {showDateStep && <>
                                            <button
                                                type='button'
                                                class='btn btn-outline-secondary rounded-pill me-1'
                                                onClick={this.onClickStepReportDate}
                                                data-direction='-1'
                                                title={`Forrige ${dateStepLabel}`}
                                            >
                                                <i class='fa-solid fa-chevron-left' />
                                            </button>
                                        </>}
                                        {view.report === 'periode' ? <>
                                            <input
                                                class='form-control rounded-pill'
                                                type='datetime-local'
                                                onInput={this.changeReportDateTime}
                                                value={reportDateTimeStart}
                                                data-name={'reportDateTimeStart'}
                                            />
                                            <input
                                                class='form-control rounded-pill'
                                                type='datetime-local'
                                                onInput={this.changeReportDateTime}
                                                value={reportDateTimeEnd}
                                                data-name={'reportDateTimeEnd'}
                                            />
                                        </> : <>
                                            <input
                                                class='form-control rounded-pill'
                                                type='date'
                                                onInput={this.changeReportDate}
                                                value={reportDate}
                                                data-name={'reportDate'}
                                            />
                                        </>}
                                        {showDateStep && <>
                                            <button
                                                type='button'
                                                class='btn btn-outline-secondary rounded-pill ms-1'
                                                onClick={this.onClickStepReportDate}
                                                data-direction='1'
                                                title={`Neste ${dateStepLabel}`}
                                            >
                                                <i class='fa-solid fa-chevron-right' />
                                            </button>
                                        </>}
                                    </div>
                                </div>
                            </div>

                            {(view.report === 'month' || view.report === 'year') && <>
                                <div class='d-flex flex-row justify-content-end px-3'>
                                    <div class='form-group form-check'>
                                        <input type='checkbox' class='form-check-input' id='fromDate' onInput={this.toggleOnlyFromDate} />
                                        <label class='form-check-label' for='fromDate'>Kun fra valgt dato</label>
                                    </div>
                                </div>
                            </>}

                            {view.report === 'accounts-yearly' && <>
                                <div class='d-flex flex-column px-3 mt-4 mb-3'>
                                    <div class='d-flex flex-row flex-wrap align-items-center mb-4 gap-2'>
                                        <label class='mb-0'>År:</label>
                                        <select
                                            class='form-select rounded-pill'
                                            style='width: auto;'
                                            value={accountsYearlyYear}
                                            onChange={this.changeAccountsYearlyYear}
                                        >
                                            {[...Array(10)].map((_, i) => {
                                                const y = new Date().getFullYear() - i;
                                                return <option key={y} value={y}>{y}</option>;
                                            })}
                                        </select>
                                        <label class='mb-0 ms-2'>Fra:</label>
                                        <select
                                            class='form-select rounded-pill'
                                            style='width: auto;'
                                            value={accountsYearlyStartMonth}
                                            onChange={this.changeAccountsYearlyStartMonth}
                                        >
                                            {['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'].map((m, i) => (
                                                <option key={i + 1} value={i + 1}>{m}</option>
                                            ))}
                                        </select>
                                        <label class='mb-0'>Til:</label>
                                        <select
                                            class='form-select rounded-pill'
                                            style='width: auto;'
                                            value={accountsYearlyEndMonth}
                                            onChange={this.changeAccountsYearlyEndMonth}
                                        >
                                            {['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'].map((m, i) => (
                                                <option key={i + 1} value={i + 1}>{m}</option>
                                            ))}
                                        </select>
                                        <button class='btn btn-outline-primary rounded-pill ms-2' onClick={this.downloadYearlyAccountsCSV}>
                                            <i class='fa-solid fa-download' /> Last ned CSV
                                        </button>
                                        <button class='btn btn-outline-primary rounded-pill' onClick={this.printYearlyAccountsReport}>
                                            <i class='fa-solid fa-print' /> Skriv ut
                                        </button>
                                    </div>
                                    {yearlyAccountsReportLoading ? <>
                                        <div class='text-center py-5'>
                                            <i class='fa-duotone fa-spinner-third fa-spin fa-2x' />
                                        </div>
                                    </> : yearlyAccountsReport ? <>
                                        <div class={`d-flex flex-column ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} p-4 rounded-lg print-section`}>
                                            <h4 class='mb-4'>
                                                Søk økonomi - {yearlyAccountsReport.startMonth === 1 && yearlyAccountsReport.endMonth === 12
                                                    ? `Hele ${yearlyAccountsReport.year}`
                                                    : `${yearlyAccountsReport.monthNames[0]} - ${yearlyAccountsReport.monthNames[yearlyAccountsReport.monthNames.length - 1]} ${yearlyAccountsReport.year}`}
                                            </h4>
                                            <div class='table-responsive'>
                                                <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                    <thead>
                                                        <tr>
                                                            <th>Konto</th>
                                                            <th>Kontonavn</th>
                                                            {yearlyAccountsReport.monthNames.map(m => <th class='text-end' key={m}>{m}</th>)}
                                                            <th class='text-end'><strong>Sum</strong></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {yearlyAccountsReport.accountRows.map(row => (
                                                            <tr key={row.account}>
                                                                <td>{row.account}</td>
                                                                <td>{getAccountName(row.account, userStore.customer) || row.account || 'Ingen konto'}</td>
                                                                {row.months.map((v, i) => <td class='text-end' key={i}>{util.format(v, 0)}</td>)}
                                                                <td class='text-end'><strong>{util.format(row.total, 0)}</strong></td>
                                                            </tr>
                                                        ))}
                                                        <tr class='table-dark'>
                                                            <td><strong>TOTALT</strong></td>
                                                            <td></td>
                                                            {yearlyAccountsReport.monthlyTotals.map((v, i) => <td class='text-end' key={i}><strong>{util.format(v, 0)}</strong></td>)}
                                                            <td class='text-end'><strong>{util.format(yearlyAccountsReport.grandTotal, 0)}</strong></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </> : <></>}
                                </div>
                            </>}

                            {view.report === 'accounts-receivable' && <>
                                <div class='d-flex flex-column px-3 mt-4 mb-3'>
                                    <div class='d-flex flex-row flex-wrap align-items-center mb-4 gap-2'>
                                        <label class='me-2 mb-0'>Fra dato (valgfritt):</label>
                                        <input
                                            class='form-control rounded-pill'
                                            style='width: auto;'
                                            type='date'
                                            value={accountsReceivableStartDate}
                                            onInput={this.changeAccountsReceivableStartDate}
                                        />
                                        <label class='ms-3 me-2 mb-0'>Utestående per dato:</label>
                                        <input
                                            class='form-control rounded-pill'
                                            style='width: auto;'
                                            type='date'
                                            value={accountsReceivableCutoffDate}
                                            onInput={this.changeAccountsReceivableCutoffDate}
                                        />
                                        <button class='btn btn-outline-primary rounded-pill ms-3' onClick={this.downloadAccountsReceivableCSV}>
                                            <i class='fa-solid fa-download' /> Last ned CSV
                                        </button>
                                        <button class='btn btn-outline-primary rounded-pill ms-2' onClick={this.printAccountsReceivableReport}>
                                            <i class='fa-solid fa-print' /> Skriv ut
                                        </button>
                                    </div>
                                    {accountsReceivableReportLoading ? <>
                                        <div class='text-center py-5'>
                                            <i class='fa-duotone fa-spinner-third fa-spin fa-2x' />
                                        </div>
                                    </> : accountsReceivableReport ? <>
                                        <div class={`d-flex flex-column ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} p-4 rounded-lg print-section`}>
                                            <h4 class='mb-1'>
                                                Utestående{accountsReceivableReport.startDate ? ` fra ${accountsReceivableReport.startDate} ` : ' '}per {accountsReceivableReport.cutoffDate}
                                            </h4>
                                            <small class='text-body-secondary mb-4 d-block'>
                                                {accountsReceivableReport.startDate
                                                    ? `Viser kun fakturaer med fakturadato fra ${accountsReceivableReport.startDate} til ${accountsReceivableReport.cutoffDate}. Aldersfordeling viser antall dager fra fakturadato til valgt dato (${accountsReceivableReport.cutoffDate}).`
                                                    : `Aldersfordeling viser antall dager fra fakturadato til valgt dato (${accountsReceivableReport.cutoffDate})`}
                                            </small>
                                            {accountsReceivableReport.truncated && <div class='alert alert-warning py-2'>
                                                <i class='fa-duotone fa-triangle-exclamation me-1' />
                                                Klinikken har svært mange salg — rapporten er basert på de 50 000 nyeste salgene til og med valgt dato. Eventuelle ubetalte fakturaer som er eldre enn dette, vises ikke.
                                            </div>}
                                            <div class='d-flex flex-row flex-wrap mb-4 gap-4'>
                                                <div class='pe-3'>
                                                    <small class='text-muted'>Totalt utestående:</small>
                                                    <div class='h4'>{util.format(accountsReceivableReport.grandTotal, 2)}</div>
                                                </div>
                                                <div class='pe-3'>
                                                    <small class='text-muted'>0-30 dager:</small>
                                                    <div class='h5'>{util.format(accountsReceivableReport.agingBuckets['0-30'].total, 2)}</div>
                                                </div>
                                                <div class='pe-3'>
                                                    <small class='text-muted'>31-60 dager:</small>
                                                    <div class='h5'>{util.format(accountsReceivableReport.agingBuckets['31-60'].total, 2)}</div>
                                                </div>
                                                <div class='pe-3'>
                                                    <small class='text-muted'>61-90 dager:</small>
                                                    <div class='h5'>{util.format(accountsReceivableReport.agingBuckets['61-90'].total, 2)}</div>
                                                </div>
                                                <div class='pe-3'>
                                                    <small class='text-muted'>Over 90 dager:</small>
                                                    <div class='h5 text-danger'>{util.format(accountsReceivableReport.agingBuckets['90+'].total, 2)}</div>
                                                </div>
                                            </div>
                                            <div class='table-responsive'>
                                                <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                    <thead>
                                                        <tr>
                                                            <th>Fakturanr</th>
                                                            <th>Kunde</th>
                                                            <th>Kundenr</th>
                                                            <th>Fakturadato</th>
                                                            <th class='text-end'>Totalbeløp</th>
                                                            <th class='text-end'>Innbetalt</th>
                                                            <th class='text-end'>Beløp</th>
                                                            <th class='text-end'>0-30 dager</th>
                                                            <th class='text-end'>31-60 dager</th>
                                                            <th class='text-end'>61-90 dager</th>
                                                            <th class='text-end'>Over 90 dager</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {accountsReceivableReport.unpaidInvoices.map(inv => (
                                                            <tr key={inv.id}>
                                                                <td>
                                                                    <a href={`#/dyrejournal-sale/${inv.id}`} target='_blank' rel='noopener noreferrer'>
                                                                        {inv.saleNumber}
                                                                    </a>
                                                                </td>
                                                                <td>{inv.visitorName}</td>
                                                                <td>{inv.visitorId}</td>
                                                                <td>{inv.saleDate ? util.isoDate(inv.saleDate, false, false, true) : ''}</td>
                                                                <td class='text-end'>{util.format(inv.saleTotal, 2)}</td>
                                                                <td class='text-end'>{util.format(inv.totalPaid, 2)}</td>
                                                                <td class='text-end'>{util.format(inv.unpaidAmount, 2)}</td>
                                                                <td class='text-end'>{inv.bucket === '0-30' ? util.format(inv.unpaidAmount, 2) : ''}</td>
                                                                <td class='text-end'>{inv.bucket === '31-60' ? util.format(inv.unpaidAmount, 2) : ''}</td>
                                                                <td class='text-end'>{inv.bucket === '61-90' ? util.format(inv.unpaidAmount, 2) : ''}</td>
                                                                <td class='text-end text-danger'>{inv.bucket === '90+' ? util.format(inv.unpaidAmount, 2) : ''}</td>
                                                            </tr>
                                                        ))}
                                                        <tr class='table-dark'>
                                                            <td><strong>TOTALT</strong></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td></td>
                                                            <td class='text-end'><strong>{util.format(accountsReceivableReport.grandTotal, 2)}</strong></td>
                                                            <td class='text-end'><strong>{util.format(accountsReceivableReport.agingBuckets['0-30'].total, 2)}</strong></td>
                                                            <td class='text-end'><strong>{util.format(accountsReceivableReport.agingBuckets['31-60'].total, 2)}</strong></td>
                                                            <td class='text-end'><strong>{util.format(accountsReceivableReport.agingBuckets['61-90'].total, 2)}</strong></td>
                                                            <td class='text-end text-danger'><strong>{util.format(accountsReceivableReport.agingBuckets['90+'].total, 2)}</strong></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            {accountsReceivableReport.unpaidInvoices.length === 0 && <>
                                                <div class='text-center text-muted py-3'>
                                                    Ingen utestående fakturaer per {accountsReceivableReport.cutoffDate}
                                                </div>
                                            </>}
                                        </div>
                                    </> : <></>}
                                </div>
                            </>}

                            {/* ===== KEY METRICS DASHBOARD ===== */}
                            {!noViewSelected && view.report !== 'accounts-yearly' && view.report !== 'accounts-receivable' && <>
                                <div class='d-flex flex-row flex-wrap px-2 mb-3'>
                                    {/* Card 1: Total Sales */}
                                    <div class='col-6 col-md-2 p-2'>
                                        <div class={`d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3 py-2 text-center`}>
                                            <div class='text-muted small'>Salg</div>
                                            <div class='h5 mb-0'>{totalNumberOfSales || 0}</div>
                                        </div>
                                    </div>
                                    {/* Card 2: Revenue */}
                                    <div class='col-6 col-md-2 p-2'>
                                        <div class={`d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3 py-2 text-center`}>
                                            <div class='text-muted small'>Omsetning</div>
                                            <div class='h5 mb-0'>{util.format(aggSaleTotal?.price, 0)}</div>
                                        </div>
                                    </div>
                                    {/* Card 3: Journals */}
                                    <div class='col-6 col-md-2 p-2'>
                                        <div class={`d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3 py-2 text-center`}>
                                            <div class='text-muted small'>Journaler</div>
                                            <div class='h5 mb-0'>{journalCount || 0}</div>
                                        </div>
                                    </div>
                                    {/* Card 4: New Customers */}
                                    <div class='col-6 col-md-2 p-2'>
                                        <div class={`d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3 py-2 text-center`}>
                                            <div class='text-muted small'>Nye kunder</div>
                                            <div class='h5 mb-0'>{aggSaleNewVisitors || 0}</div>
                                        </div>
                                    </div>
                                    {/* Card 5: Active Customers */}
                                    <div class='col-6 col-md-2 p-2'>
                                        <div class={`d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3 py-2 text-center`}>
                                            <div class='text-muted small'>Aktive kunder</div>
                                            <div class='h5 mb-0'>{aggSaleActiveVisitors || 0}</div>
                                        </div>
                                    </div>
                                    {/* Card 6: Discounts */}
                                    <div class='col-6 col-md-2 p-2'>
                                        <div class={`d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3 py-2 text-center`}>
                                            <div class='text-muted small'>Rabatt gitt</div>
                                            <div class='h5 mb-0'>{util.format(aggSaleTotalDiscount?.price, 0)}</div>
                                        </div>
                                    </div>
                                    {/* Card 7: Collective invoice — work registered but not yet invoiced */}
                                    {aggSaleTotalCollectiveInvoice?.price > 0 && <div class='col-6 col-md-2 p-2'>
                                        <div class={`d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3 py-2 text-center`} title='Samlefaktura som ikke er fakturert ennå (potensial). Ikke inkludert i Omsetning.'>
                                            <div class='text-warning-emphasis small'>Til fakturering</div>
                                            <div class='h5 mb-0 text-warning-emphasis'>{util.format(aggSaleTotalCollectiveInvoice?.price, 0)}</div>
                                        </div>
                                    </div>}
                                </div>

                                {/* ===== CHARTS ROW ===== */}
                                <div class='d-flex flex-column flex-lg-row gap-3 px-3 mb-3'>
                                    {/* Payment Method Breakdown (Bar Chart) */}
                                    <div class='flex-fill'>
                                        <div class='font-weight-lighter px-3 box-header mb-1'>
                                            Fordeling per betalingsmetode
                                        </div>
                                        <div class={`d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3 py-3`}>
                                            {aggSaleTotalPaymentMethods && aggSaleTotalPaymentMethods.length > 0 ? <>
                                                {(() => {
                                                    // Add partial credit note to the payment methods list if it has a value
                                                    const partialCreditNote = saleStore.getPaymentMethodTotalNew('creditNote');
                                                    const extendedPaymentMethods = [...aggSaleTotalPaymentMethods];
                                                    if (partialCreditNote && partialCreditNote !== 0) {
                                                        extendedPaymentMethods.push({
                                                            paymentMethod: 'partialCreditNote',
                                                            price: partialCreditNote,
                                                        });
                                                    }
                                                    const maxPayment = Math.max(...extendedPaymentMethods.map(pm => Math.abs(pm.price || 0)));
                                                    return extendedPaymentMethods.map((pm, idx) => {
                                                        const price = pm.price || 0;
                                                        const widthPct = maxPayment > 0 ? (Math.abs(price) / maxPayment) * 100 : 0;
                                                        const isNegative = price < 0;
                                                        const colors = ['#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f', '#edc949', '#af7aa1', '#ff9da7'];
                                                        const color = isNegative ? '#e15759' : colors[idx % colors.length];
                                                        return (
                                                            <div class='d-flex flex-row align-items-center mb-2' key={pm.paymentMethod}>
                                                                <div style='width: 60px; font-size: 0.75em;' class='text-muted'>
                                                                    {saleStore.getPaymentMethodText(pm.paymentMethod)}
                                                                </div>
                                                                <div class='flex-fill mx-2'>
                                                                    <div style={`height: 16px; width: ${widthPct}%; background-color: ${color}; border-radius: 4px; min-width: 2px;${isNegative ? ' opacity: 0.7;' : ''}`}></div>
                                                                </div>
                                                                <div style={`width: 70px; text-align: right; font-size: 0.75em;${isNegative ? ' color: #e15759;' : ''}`}>
                                                                    {util.format(price, 0)}
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                            </> : <div class='text-muted small'>Ingen data</div>}
                                        </div>
                                    </div>

                                    {/* Revenue Trend (Bar Chart) */}
                                    <div class='flex-fill'>
                                        <div class='font-weight-lighter px-3 box-header mb-1'>
                                            Omsetning siste 12 måneder
                                        </div>
                                        <div class={`d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3 py-3`}>
                                            {saleTrends && saleTrends.length > 0 ? <>
                                                {(() => {
                                                    const chartHeight = 100;
                                                    // Separate scales for revenue and sales count
                                                    const revenueValues = saleTrends.map(t => t.y || 0);
                                                    const saleCountValues = saleTrends.map(t => t.saleCount || 0);
                                                    const maxRevenue = Math.max(...revenueValues) || 1;
                                                    const maxSaleCount = Math.max(...saleCountValues) || 1;
                                                    return (
                                                        <>
                                                            <div class='d-flex flex-row justify-content-end mb-2' style='font-size: 0.7em; gap: 15px;'>
                                                                <span><span style='display: inline-block; width: 12px; height: 12px; background-color: #e15759; border-radius: 2px; margin-right: 4px;'></span>Antall salg</span>
                                                                <span><span style='display: inline-block; width: 12px; height: 12px; background-color: #4e79a7; border-radius: 2px; margin-right: 4px;'></span>Omsetning</span>
                                                            </div>
                                                            <div style={`display: flex; flex-direction: row; align-items: flex-end; height: ${chartHeight}px; gap: 6px;`}>
                                                                {saleTrends.map((item, idx) => {
                                                                    const revenueHeight = Math.max((item.y / maxRevenue) * chartHeight, item.y > 0 ? 4 : 2);
                                                                    const saleCountHeight = Math.max((item.saleCount / maxSaleCount) * chartHeight, item.saleCount > 0 ? 4 : 2);
                                                                    return (
                                                                        <div style='flex: 1; display: flex; flex-direction: row; align-items: flex-end; gap: 2px;' key={idx}>
                                                                            <div style={`flex: 1; height: ${saleCountHeight}px; background-color: #e15759; border-radius: 2px 2px 0 0;`} title={`${item.month} ${item.year}: ${item.saleCount} salg`} />
                                                                            <div style={`flex: 1; height: ${revenueHeight}px; background-color: #4e79a7; border-radius: 2px 2px 0 0;`} title={`${item.month} ${item.year}: ${util.format(item.y, 0)} kr`} />
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            <div style='display: flex; flex-direction: row; gap: 6px; margin-top: 4px;'>
                                                                {saleTrends.map((item, idx) => (
                                                                    <div style='flex: 1; text-align: center; font-size: 0.6em;' class='text-muted' key={idx}>
                                                                        {item.month}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                                <div class='d-flex flex-row justify-content-between mt-2' style='font-size: 0.7em;'>
                                                    <span class='text-muted'>
                                                        <span style='display: inline-block; width: 8px; height: 8px; background-color: #e15759; border-radius: 2px; margin-right: 3px;'></span>
                                                        Min: {Math.min(...saleTrends.filter(t => t.saleCount > 0).map(t => t.saleCount))} / Max: {Math.max(...saleTrends.map(t => t.saleCount || 0))} salg
                                                    </span>
                                                    <span class='text-muted'>
                                                        <span style='display: inline-block; width: 8px; height: 8px; background-color: #4e79a7; border-radius: 2px; margin-right: 3px;'></span>
                                                        Min: {util.format(Math.min(...saleTrends.filter(t => t.y > 0).map(t => t.y)), 0)} / Max: {util.format(Math.max(...saleTrends.map(t => t.y || 0)), 0)} kr
                                                    </span>
                                                </div>
                                            </> : <div class='text-muted small'>Laster data...</div>}
                                        </div>
                                    </div>

                                    {/* Customer Trends (Bar Chart) */}
                                    <div class='flex-fill'>
                                        <div class='font-weight-lighter px-3 box-header mb-1 d-flex flex-row justify-content-between align-items-center'>
                                            Kunder siste 12 måneder
                                            <a
                                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                                href={`${apiServer}/api/sales/visitor-trends?jwtToken=${jwtToken}&months=12&format=csv`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                title='Last ned CSV'
                                            >
                                                <i class='fa-solid fa-download' />
                                            </a>
                                        </div>
                                        <div class={`d-flex flex-column rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-3 py-3`}>
                                        {visitorTrends && visitorTrends.length > 0 ? <>
                                            {(() => {
                                                const chartHeight = 100;
                                                // Find max from all series for shared scale
                                                const allValues = [
                                                    ...visitorTrends.map(t => t.newCustomers || 0),
                                                    ...visitorTrends.map(t => t.activeCustomers || 0),
                                                    ...visitorTrends.map(t => t.activePatients || 0),
                                                ];
                                                const maxValue = Math.max(...allValues) || 1;
                                                return (
                                                    <>
                                                        <div class='d-flex flex-row justify-content-end mb-2' style='font-size: 0.7em; gap: 15px;'>
                                                            <span><span style='display: inline-block; width: 12px; height: 12px; background-color: #e15759; border-radius: 2px; margin-right: 4px;'></span>Nye kunder</span>
                                                            <span><span style='display: inline-block; width: 12px; height: 12px; background-color: #4e79a7; border-radius: 2px; margin-right: 4px;'></span>Aktive kunder</span>
                                                            <span><span style='display: inline-block; width: 12px; height: 12px; background-color: #59a14f; border-radius: 2px; margin-right: 4px;'></span>Aktive pasienter</span>
                                                        </div>
                                                        <div style={`display: flex; flex-direction: row; align-items: flex-end; height: ${chartHeight}px; gap: 6px;`}>
                                                            {visitorTrends.map((item, idx) => {
                                                                const newHeight = Math.max((item.newCustomers / maxValue) * chartHeight, item.newCustomers > 0 ? 4 : 2);
                                                                const activeHeight = Math.max((item.activeCustomers / maxValue) * chartHeight, item.activeCustomers > 0 ? 4 : 2);
                                                                const patientHeight = Math.max(((item.activePatients || 0) / maxValue) * chartHeight, (item.activePatients || 0) > 0 ? 4 : 2);
                                                                return (
                                                                    <div style='flex: 1; display: flex; flex-direction: row; align-items: flex-end; gap: 1px;' key={idx}>
                                                                        <div style={`flex: 1; height: ${newHeight}px; background-color: #e15759; border-radius: 2px 2px 0 0;`} title={`${item.month} ${item.year}: ${item.newCustomers} nye kunder`} />
                                                                        <div style={`flex: 1; height: ${activeHeight}px; background-color: #4e79a7; border-radius: 2px 2px 0 0;`} title={`${item.month} ${item.year}: ${item.activeCustomers} aktive kunder`} />
                                                                        <div style={`flex: 1; height: ${patientHeight}px; background-color: #59a14f; border-radius: 2px 2px 0 0;`} title={`${item.month} ${item.year}: ${item.activePatients || 0} aktive pasienter`} />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        <div style='display: flex; flex-direction: row; gap: 6px; margin-top: 4px;'>
                                                            {visitorTrends.map((item, idx) => (
                                                                <div style='flex: 1; text-align: center; font-size: 0.6em;' class='text-muted' key={idx}>
                                                                    {item.month}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                            <div class='d-flex flex-row justify-content-between mt-2' style='font-size: 0.7em;'>
                                                <span class='text-muted'>
                                                    <span style='display: inline-block; width: 8px; height: 8px; background-color: #e15759; border-radius: 2px; margin-right: 3px;'></span>
                                                    Min: {Math.min(...visitorTrends.filter(t => t.newCustomers > 0).map(t => t.newCustomers))} / Max: {Math.max(...visitorTrends.map(t => t.newCustomers || 0))} nye
                                                </span>
                                                <span class='text-muted'>
                                                    <span style='display: inline-block; width: 8px; height: 8px; background-color: #4e79a7; border-radius: 2px; margin-right: 3px;'></span>
                                                    Min: {Math.min(...visitorTrends.filter(t => t.activeCustomers > 0).map(t => t.activeCustomers))} / Max: {Math.max(...visitorTrends.map(t => t.activeCustomers || 0))} aktive
                                                </span>
                                                <span class='text-muted'>
                                                    <span style='display: inline-block; width: 8px; height: 8px; background-color: #59a14f; border-radius: 2px; margin-right: 3px;'></span>
                                                    Min: {Math.min(...visitorTrends.filter(t => (t.activePatients || 0) > 0).map(t => t.activePatients || 0))} / Max: {Math.max(...visitorTrends.map(t => t.activePatients || 0))} pasienter
                                                </span>
                                            </div>
                                        </> : <div class='text-muted small'>Laster data...</div>}
                                        </div>
                                    </div>
                                </div>
                            </>}


                            {view.report !== 'accounts-yearly' && view.report !== 'accounts-receivable' && <>
                                <div class='d-flex flex-row justify-content-between mt-4'>
                                    <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                        <Text id='dyrejournal.report.all-sales'>Total sales</Text>
                                    </div>
                                    <div class='d-flex flex-row'>
                                        <div class='form-check mx-2'>
                                            <input type='checkbox' class='form-check-input' id='showCustomerNumber' checked={showCustomerNumber} onInput={this.toggleShowCustomerNumber} />
                                            <label class='form-check-label text-nowrap' for='showCustomerNumber'>
                                                <Text id='dyrejournal.report.show-customer-number'>Show customer number</Text>
                                            </label>
                                        </div>
                                        <select
                                            class='form-control form-control-sm rounded-pill mx-2'
                                            style='max-width: 180px;'
                                            value={activePaymentMethod}
                                            onChange={linkstate(this, 'activePaymentMethod')}
                                        >
                                            <option value=''><Text id='dyrejournal.select-sale-payment-method'>-- Choose payment method --</Text></option>
                                            {Object.keys(saleStore.paymentMethodsFilter).map(e => (
                                                <option key={e} value={e}>{saleStore.getPaymentMethodText(e)}</option>
                                            ))}
                                        </select>
                                        <input
                                            type='text'
                                            class='form-control form-control-sm mx-2'
                                            style='max-width: 180px;'
                                            placeholder='Filtrer varenavn...'
                                            value={rawDataProductSearch}
                                            onInput={linkstate(this, 'rawDataProductSearch')}
                                        />
                                        <a
                                            class='btn btn-sm btn-outline-secondary rounded-pill mx-2'
                                            href={`${apiServer}/api/sales/csv/raw-data?jwtToken=${jwtToken}&reportDate=${reportDate}&type=${view.report}&onlyFromDate=${onlyFromDate || false}&startDate=${view.report === 'periode' ? reportDateTimeStart : ''}&endDate=${view.report === 'periode' ? reportDateTimeEnd : ''}&productSearch=${encodeURIComponent(rawDataProductSearch)}&paymentMethod=${activePaymentMethod || ''}`}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            <i class='fa-duotone fa-download' /> Raw data CSV
                                        </a>
                                        <button
                                            class='btn btn-sm btn-outline-primary rounded-pill mx-2'
                                            data-date={reportDate}
                                            data-datefrom={view.report === 'periode' ? reportDateTimeStart : undefined}
                                            data-dateto={view.report === 'periode' ? reportDateTimeEnd : undefined}
                                            data-type={view.report}
                                            data-onlyfromdate={onlyFromDate}
                                            data-paymentmethod={activePaymentMethod || ''}
                                            onClick={this.onClickAllSales}
                                        >
                                            <i class='fa-duotone fa-eye' /> <Text id='dyrejournal.report.view-all-sales'>View all sales</Text>
                                        </button>
                                    </div>
                                </div>
                                <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                {noViewSelected && <>
                                    <div class='alert alert-danger' role='alert'>
                                        <Text id='dyrejournal.report.no-view-selected'>No view selected. Please select daily, monthly or yearly above.</Text>
                                    </div>
                                </>}

                                {(view.report === 'day' || view.report === 'periode') && <>
                                    {saleReport.openingCash != null && <div class='alert alert-info'>
                                        <Text id='dyrejournal.report.cash-carry-forward-help'>
                                            Opening cash comes from the previous saved daily report.
                                            Cash payments on intervening days are included.
                                            Save the daily report after adding change or withdrawing cash to the bank.
                                            Enter the total added change and bank withdrawals for the selected day.
                                        </Text>
                                        {saleReport.cashCarryForwardReport === 0 && <div class='mt-2'>
                                            <Text id='dyrejournal.report.cash-carry-forward-empty'>
                                                No previous daily report with a known department was found.
                                                Opening cash starts at zero. Register any starting cash
                                                as Added change in the first report.
                                            </Text>
                                        </div>}
                                    </div>}
                                    <div class='form-check mb-2'>
                                        <input type='checkbox' class='form-check-input' id='hideCreditSales' checked={hideCreditSales} onInput={this.toggleHideCreditSales} />
                                        <label class='form-check-label' for='hideCreditSales'>
                                            <Text id='dyrejournal.report.hide-credit-sales'>Hide credit sales</Text>
                                        </label>
                                        <small class='text-body-secondary ms-2'>
                                            <Text id='dyrejournal.report.hide-credit-sales-help'>Shows only cash, card and Vipps in the table and the reconciliation. The print-out and the saved report are not affected.</Text>
                                        </small>
                                    </div>
                                    <DyrejournalReportSalesAndPayments
                                        saleReport={saleReport}
                                        reportType={view.report}
                                        showCreditCard={showCreditCard}
                                        showConvene={showConvene}
                                        hideCreditSales={hideCreditSales}
                                        editable
                                        inputFor={this.inputFor}
                                        cardBreakdown={cardBreakdown}
                                        onAnalyze={this.onClickShowCashflowAnalysis}
                                        cashflowAnalysis={cashflowAnalysis}
                                        cashflowAnalysisLoading={cashflowAnalysisLoading}
                                    />
                                    {/* Card Analysis (NETS) */}
                                    <div class='mt-3'>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-secondary'
                                            onClick={this.onClickShowCardBreakdown}
                                        >
                                            <i class='fa-duotone fa-credit-card me-2' />
                                            <Text id='dyrejournal.report.analyze-card-payments'>Analyze card payments (NETS)</Text>
                                            <span class='badge bg-info text-dark ms-2'><Text id='dyrejournal.report.experimental'>Experimental</Text></span>
                                        </button>
                                    </div>
                                    {showCardBreakdown && <div class='mt-3 p-3 border rounded bg-body-secondary'>
                                        <div class='d-flex justify-content-between align-items-center mb-3'>
                                            <h6 class='mb-0 border-bottom pb-2 flex-grow-1'>
                                                <i class='fa-duotone fa-credit-card me-2' />
                                                <Text id='dyrejournal.report.card-breakdown'>Card breakdown (NETS)</Text>
                                            </h6>
                                            <button type='button' class='btn btn-sm btn-link text-body-secondary' onClick={this.onClickCloseCardBreakdown}>
                                                <i class='fa-solid fa-times' />
                                            </button>
                                        </div>
                                        {cardBreakdownLoading ? (
                                            <div class='text-center py-3'>
                                                <i class='fa-duotone fa-spinner fa-spin me-2' />
                                                <Text id='dyrejournal.report.loading'>Loading...</Text>
                                            </div>
                                        ) : cardBreakdown ? (
                                            <>
                                                <div class='small text-body-secondary mb-2'>
                                                    <Text id='dyrejournal.report.card-breakdown-help'>Based on NETS terminal responses. Only approved transactions are counted. Amounts use the terminal's reported totalAmount.</Text>
                                                </div>

                                                <div class='table-responsive mt-3'>
                                                    <h6 class='mb-2 border-bottom'><Text id='dyrejournal.report.card-category'>By category</Text></h6>
                                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em;'>
                                                        <thead>
                                                            <tr>
                                                                <th><Text id='dyrejournal.report.category'>Category</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.share'>Share</Text></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {cardBreakdown.byCategory?.map(c => (
                                                                <tr>
                                                                    <td>
                                                                        {c.category === 'debit-bankaxept' && <Text id='dyrejournal.report.debit-bankaxept'>BankAxept (debit)</Text>}
                                                                        {c.category === 'debit-card' && <Text id='dyrejournal.report.debit-card'>Debit card (Visa/Mastercard)</Text>}
                                                                        {c.category === 'credit-card' && <Text id='dyrejournal.report.credit-card'>Credit card</Text>}
                                                                        {c.category === 'unknown' && <Text id='dyrejournal.report.unknown'>Unknown</Text>}
                                                                    </td>
                                                                    <td style='text-align: right;'>{c.count}</td>
                                                                    <td style='text-align: right;'><nobr>{util.format(c.amount, 2)}</nobr></td>
                                                                    <td style='text-align: right;'>
                                                                        {cardBreakdown.totalAmount > 0
                                                                            ? `${((c.amount / cardBreakdown.totalAmount) * 100).toFixed(1)}%`
                                                                            : '-'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            <tr style='border-top: 1px solid #808080;'>
                                                                <td><strong><Text id='dyrejournal.report.total'>Total</Text></strong></td>
                                                                <td style='text-align: right;'><strong>{cardBreakdown.totalTransactions}</strong></td>
                                                                <td style='text-align: right;'><strong><nobr>{util.format(cardBreakdown.totalAmount, 2)}</nobr></strong></td>
                                                                <td style='text-align: right;'><strong>100%</strong></td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <div class='table-responsive mt-4'>
                                                    <h6 class='mb-2 border-bottom'><Text id='dyrejournal.report.card-issuer'>By issuer bank</Text></h6>
                                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em;'>
                                                        <thead>
                                                            <tr>
                                                                <th><Text id='dyrejournal.report.issuer'>Issuer</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.share'>Share</Text></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {cardBreakdown.byIssuer?.map(row => (
                                                                <tr>
                                                                    <td>{row.issuer}</td>
                                                                    <td style='text-align: right;'>{row.count}</td>
                                                                    <td style='text-align: right;'><nobr>{util.format(row.amount, 2)}</nobr></td>
                                                                    <td style='text-align: right;'>
                                                                        {cardBreakdown.totalAmount > 0
                                                                            ? `${((row.amount / cardBreakdown.totalAmount) * 100).toFixed(1)}%`
                                                                            : '-'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {cardBreakdown.unknownByCardType?.length > 0 && (
                                                    <div class='table-responsive mt-4'>
                                                        <h6 class='mb-2 border-bottom'>
                                                            <Text id='dyrejournal.report.unknown-by-cardtype'>Unclassified by card type (normalized)</Text>
                                                        </h6>
                                                        <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.cardtype'>Card type</Text></th>
                                                                    <th><Text id='dyrejournal.report.scheme'>Scheme</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cardBreakdown.unknownByCardType.map(row => (
                                                                    <tr>
                                                                        <td><code>{row.cardType}</code></td>
                                                                        <td>{row.scheme}</td>
                                                                        <td style='text-align: right;'>{row.count}</td>
                                                                        <td style='text-align: right;'><nobr>{util.format(row.amount, 2)}</nobr></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {cardBreakdown.unknownBins?.length > 0 && (
                                                    <div class='table-responsive mt-4'>
                                                        <h6 class='mb-2 border-bottom'>
                                                            <Text id='dyrejournal.report.unknown-bins'>Unclassified BINs</Text>
                                                        </h6>
                                                        <div class='alert alert-info small mb-2'>
                                                            <i class='fa-duotone fa-circle-info me-2' />
                                                            <Text id='dyrejournal.report.unknown-bins-help'>
                                                                These card transactions could not be automatically classified as debit or credit because the terminal only reported a generic card type (e.g. "Mastercard" or "VISA") without a debit/credit hint. Each row shows the first 6 digits of the card number (BIN — Bank Identification Number), which identifies the issuing bank and card product. They still count toward totals and the issuer breakdown above, but appear under the "Unknown" category. To classify them, add the BIN to BIN_TABLE in backend/lib/card-classifier.js with the issuer name and type ('debit' or 'credit'). BIN info can be looked up on services like binlist.net or bincheck.io.
                                                            </Text>
                                                        </div>
                                                        <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.bin'>BIN</Text></th>
                                                                    <th><Text id='dyrejournal.report.cardtype'>Card type</Text></th>
                                                                    <th><Text id='dyrejournal.report.scheme'>Scheme</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cardBreakdown.unknownBins.map(row => (
                                                                    <tr>
                                                                        <td><code>{row.bin}</code></td>
                                                                        <td>{row.cardType}</td>
                                                                        <td>{row.scheme}</td>
                                                                        <td style='text-align: right;'>{row.count}</td>
                                                                        <td style='text-align: right;'><nobr>{util.format(row.amount, 2)}</nobr></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div class='text-center py-3 text-body-secondary'>
                                                <Text id='dyrejournal.report.no-card-data'>No NETS transactions found for this period.</Text>
                                            </div>
                                        )}
                                    </div>}

                                    {/* Cash Flow Analysis Button */}
                                    <div class='mt-3'>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-secondary'
                                            onClick={this.onClickShowWriteoffs}
                                        >
                                            <i class='fa-duotone fa-file-slash me-2' />
                                            <Text id='dyrejournal.report.show-writeoffs'>Write-offs (Tapsføringer)</Text>
                                        </button>
                                    </div>

                                    {/* Cash Flow Analysis Results */}
                                    {showCashflowAnalysis && <div class='mt-3 p-3 border rounded bg-light'>
                                        <div class='d-flex justify-content-between align-items-center mb-3'>
                                            <h6 class='mb-0 border-bottom pb-2 flex-grow-1'>
                                                <i class='fa-duotone fa-chart-line-up me-2' />
                                                <Text id='dyrejournal.report.cashflow-analysis'>Cash Flow Analysis</Text>
                                                <span class='badge bg-warning text-dark ms-2'><Text id='dyrejournal.report.experimental'>Experimental</Text></span>
                                            </h6>
                                            <button type='button' class='btn btn-sm btn-link text-muted' onClick={this.onClickCloseCashflowAnalysis}>
                                                <i class='fa-solid fa-times' />
                                            </button>
                                        </div>
                                        <div class='alert alert-warning small mb-3'>
                                            <i class='fa-duotone fa-triangle-exclamation me-2' />
                                            <Text id='dyrejournal.report.experimental-note'>This feature is experimental and may not account for 100% of the difference. Use with caution.</Text>
                                        </div>

                                        {cashflowAnalysisLoading ? (
                                            <div class='text-center py-3'>
                                                <i class='fa-duotone fa-spinner fa-spin me-2' />
                                                <Text id='dyrejournal.report.loading'>Loading...</Text>
                                            </div>
                                        ) : cashflowAnalysis ? (
                                            <>
                                                {/* Payments from previous periods */}
                                                {cashflowAnalysis.increasesActual?.paymentsFromPreviousPeriods?.count > 0 && (
                                                    <div class='mb-3'>
                                                        <h6 class='small text-muted mb-2'>
                                                            <i class='fa-duotone fa-clock-rotate-left me-2' />
                                                            <Text id='dyrejournal.report.payments-from-previous'>Payments from Previous Periods</Text>
                                                            <span class='badge bg-success ms-2'>{cashflowAnalysis.increasesActual.paymentsFromPreviousPeriods.count}</span>
                                                        </h6>
                                                        <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.sale-period'>Sale Period</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cashflowAnalysis.increasesActual.paymentsFromPreviousPeriods.byMonth?.map(m => (
                                                                    <>
                                                                        <tr key={m.month}
                                                                            style='cursor: pointer;'
                                                                            onClick={() => this.toggleCashflowSection(`prev-${m.month}`)}
                                                                        >
                                                                            <td>
                                                                                <i class={`fa-duotone ${expandedCashflowSections[`prev-${m.month}`] ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style='font-size: 0.8em;' />
                                                                                {m.month}
                                                                            </td>
                                                                            <td style='text-align: right;'>{m.count}</td>
                                                                            <td style='text-align: right;'>{util.format(m.total, 2)}</td>
                                                                        </tr>
                                                                        {expandedCashflowSections[`prev-${m.month}`] && m.sales?.map(s => (
                                                                            <tr key={s.paymentId} class='text-body-secondary' style='font-size: 0.9em;'>
                                                                                <td class='ps-4'>
                                                                                    <a href={`/dyrejournal-sale/${s.saleId}`} onClick={e => this.onClickGotoSale(e, s.saleId)}>
                                                                                        #{s.saleNumber}
                                                                                    </a>
                                                                                    {s.visitorName ? <span class='ms-2'>{s.visitorName}</span> : null}
                                                                                    <small class='text-body-tertiary ms-2'>{util.isoDate(s.saleDate, false, false, true)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>
                                                                                    <small>{saleStore.getPaymentMethodText(s.paymentMethod)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>{util.format(s.paymentAmount, 2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </>
                                                                ))}
                                                                <tr class='fw-bold'>
                                                                    <td><Text id='dyrejournal.report.total'>Total</Text></td>
                                                                    <td style='text-align: right;'>{cashflowAnalysis.increasesActual.paymentsFromPreviousPeriods.count}</td>
                                                                    <td style='text-align: right;'>{util.format(cashflowAnalysis.increasesActual.paymentsFromPreviousPeriods.total, 2)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {/* Payments for invoice/partial sales */}
                                                {cashflowAnalysis.increasesActual?.paymentsForInvoiceSales?.count > 0 && (
                                                    <div class='mb-3'>
                                                        <h6 class='small text-muted mb-2'>
                                                            <i class='fa-duotone fa-file-invoice me-2' />
                                                            <Text id='dyrejournal.report.payments-for-invoice-sales'>Payments for Invoice/Partial Sales</Text>
                                                            <span class='badge bg-success ms-2'>{cashflowAnalysis.increasesActual.paymentsForInvoiceSales.count}</span>
                                                        </h6>
                                                        <p class='small text-muted mb-2'>
                                                            <Text id='dyrejournal.report.invoice-payments-explanation'>
                                                                Sales marked as invoice/partial, but paid with cash/card/vipps/account
                                                            </Text>
                                                        </p>
                                                        <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.sale-payment-method'>Sale Payment Method</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cashflowAnalysis.increasesActual.paymentsForInvoiceSales.byMethod?.map(m => (
                                                                    <>
                                                                        <tr key={m.salePaymentMethod}
                                                                            style='cursor: pointer;'
                                                                            onClick={() => this.toggleCashflowSection(`inv-${m.salePaymentMethod}`)}
                                                                        >
                                                                            <td>
                                                                                <i class={`fa-duotone ${expandedCashflowSections[`inv-${m.salePaymentMethod}`] ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style='font-size: 0.8em;' />
                                                                                {saleStore.getPaymentMethodText(m.salePaymentMethod)}
                                                                            </td>
                                                                            <td style='text-align: right;'>{m.count}</td>
                                                                            <td style='text-align: right;'>{util.format(m.total, 2)}</td>
                                                                        </tr>
                                                                        {expandedCashflowSections[`inv-${m.salePaymentMethod}`] && m.sales?.map(s => (
                                                                            <tr key={s.paymentId} class='text-body-secondary' style='font-size: 0.9em;'>
                                                                                <td class='ps-4'>
                                                                                    <a href={`/dyrejournal-sale/${s.saleId}`} onClick={e => this.onClickGotoSale(e, s.saleId)}>
                                                                                        #{s.saleNumber}
                                                                                    </a>
                                                                                    {s.visitorName ? <span class='ms-2'>{s.visitorName}</span> : null}
                                                                                    <small class='text-body-tertiary ms-2'>{util.isoDate(s.saleDate, false, false, true)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>
                                                                                    <small>{saleStore.getPaymentMethodText(s.paymentMethod)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>{util.format(s.paymentAmount, 2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </>
                                                                ))}
                                                                <tr class='fw-bold'>
                                                                    <td><Text id='dyrejournal.report.total'>Total</Text></td>
                                                                    <td style='text-align: right;'>{cashflowAnalysis.increasesActual.paymentsForInvoiceSales.count}</td>
                                                                    <td style='text-align: right;'>{util.format(cashflowAnalysis.increasesActual.paymentsForInvoiceSales.total, 2)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {/* Unpaid cash flow sales */}
                                                {cashflowAnalysis.decreasesActual?.unpaidCashFlowSales?.count > 0 && (
                                                    <div class='mb-3'>
                                                        <h6 class='small text-muted mb-2'>
                                                            <i class='fa-duotone fa-hourglass-half me-2' />
                                                            <Text id='dyrejournal.report.unpaid-cashflow-sales-title'>Unpaid Sales (marked cash/card/vipps/account)</Text>
                                                            <span class='badge bg-warning text-dark ms-2'>{cashflowAnalysis.decreasesActual.unpaidCashFlowSales.count}</span>
                                                        </h6>
                                                        <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.payment-method'>Payment Method</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.unpaid-amount'>Unpaid Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cashflowAnalysis.decreasesActual.unpaidCashFlowSales.byMethod?.map(m => (
                                                                    <>
                                                                        <tr key={m.paymentMethod}
                                                                            style='cursor: pointer;'
                                                                            onClick={() => this.toggleCashflowSection(`unpaid-${m.paymentMethod}`)}
                                                                        >
                                                                            <td>
                                                                                <i class={`fa-duotone ${expandedCashflowSections[`unpaid-${m.paymentMethod}`] ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style='font-size: 0.8em;' />
                                                                                {saleStore.getPaymentMethodText(m.paymentMethod)}
                                                                            </td>
                                                                            <td style='text-align: right;'>{m.count}</td>
                                                                            <td style='text-align: right;'>{util.format(m.total, 2)}</td>
                                                                        </tr>
                                                                        {expandedCashflowSections[`unpaid-${m.paymentMethod}`] && m.sales?.map(s => (
                                                                            <tr key={s.saleId} class='text-body-secondary' style='font-size: 0.9em;'>
                                                                                <td class='ps-4'>
                                                                                    <a href={`/dyrejournal-sale/${s.saleId}`} onClick={e => this.onClickGotoSale(e, s.saleId)}>
                                                                                        #{s.saleNumber}
                                                                                    </a>
                                                                                    {s.visitorName ? <span class='ms-2'>{s.visitorName}</span> : null}
                                                                                    <small class='text-body-tertiary ms-2'>{util.isoDate(s.saleDate, false, false, true)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>
                                                                                    <small>
                                                                                        <Text id='dyrejournal.report.paid'>Paid</Text>: {util.format(s.totalPaid || 0, 2)}
                                                                                        {' / '}
                                                                                        {util.format(s.saleTotal || 0, 2)}
                                                                                    </small>
                                                                                </td>
                                                                                <td style='text-align: right;'>{util.format(s.unpaidAmount, 2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </>
                                                                ))}
                                                                <tr class='fw-bold'>
                                                                    <td><Text id='dyrejournal.report.total'>Total</Text></td>
                                                                    <td style='text-align: right;'>{cashflowAnalysis.decreasesActual.unpaidCashFlowSales.count}</td>
                                                                    <td style='text-align: right;'>{util.format(cashflowAnalysis.decreasesActual.unpaidCashFlowSales.total, 2)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {/* Payment method mismatches */}
                                                {cashflowAnalysis.paymentMethodMismatch?.count > 0 && (
                                                    <div class='mb-3'>
                                                        <h6 class='small text-muted mb-2'>
                                                            <i class='fa-duotone fa-shuffle me-2' />
                                                            <Text id='dyrejournal.report.payment-method-mismatch-title'>Payment Method Mismatch</Text>
                                                            <span class='badge bg-info ms-2'>{cashflowAnalysis.paymentMethodMismatch.count}</span>
                                                        </h6>
                                                        <p class='small text-muted mb-2'>
                                                            <Text id='dyrejournal.report.payment-method-mismatch-desc'>Sales marked with one payment method but paid with a different method. This moves money between methods without affecting the total.</Text>
                                                        </p>
                                                        <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.from-method'>From Method</Text></th>
                                                                    <th><Text id='dyrejournal.report.to-method'>To Method</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cashflowAnalysis.paymentMethodMismatch.byFromTo?.map(m => (
                                                                    <>
                                                                        <tr key={`${m.fromMethod}-${m.toMethod}`}
                                                                            style='cursor: pointer;'
                                                                            onClick={() => this.toggleCashflowSection(`mm-${m.fromMethod}-${m.toMethod}`)}
                                                                        >
                                                                            <td>
                                                                                <i class={`fa-duotone ${expandedCashflowSections[`mm-${m.fromMethod}-${m.toMethod}`] ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style='font-size: 0.8em;' />
                                                                                {saleStore.getPaymentMethodText(m.fromMethod)}
                                                                            </td>
                                                                            <td>{saleStore.getPaymentMethodText(m.toMethod)}</td>
                                                                            <td style='text-align: right;'>{m.count}</td>
                                                                            <td style='text-align: right;'>{util.format(m.total, 2)}</td>
                                                                        </tr>
                                                                        {expandedCashflowSections[`mm-${m.fromMethod}-${m.toMethod}`] && m.sales?.map(s => (
                                                                            <tr key={s.paymentId} class='text-body-secondary' style='font-size: 0.9em;'>
                                                                                <td class='ps-4' colspan='2'>
                                                                                    <a href={`/dyrejournal-sale/${s.saleId}`} onClick={e => this.onClickGotoSale(e, s.saleId)}>
                                                                                        #{s.saleNumber}
                                                                                    </a>
                                                                                    {s.visitorName ? <span class='ms-2'>{s.visitorName}</span> : null}
                                                                                    <small class='text-body-tertiary ms-2'>{util.isoDate(s.saleDate, false, false, true)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>
                                                                                    <small>{saleStore.getPaymentMethodText(s.paymentMethod)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>{util.format(s.paymentAmount, 2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        {cashflowAnalysis.paymentMethodMismatch.perMethod?.length > 0 && (
                                                            <div class='mt-2'>
                                                                <p class='small text-muted mb-1'><Text id='dyrejournal.report.net-effect-per-method'>Net effect per method:</Text></p>
                                                                <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                                    <thead>
                                                                        <tr>
                                                                            <th><Text id='dyrejournal.report.method'>Method</Text></th>
                                                                            <th style='text-align: right;'><Text id='dyrejournal.report.lost'>Lost (from expected)</Text></th>
                                                                            <th style='text-align: right;'><Text id='dyrejournal.report.gained'>Gained (to actual)</Text></th>
                                                                            <th style='text-align: right;'><Text id='dyrejournal.report.net-effect'>Net Effect</Text></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {cashflowAnalysis.paymentMethodMismatch.perMethod?.map(m => (
                                                                            <tr key={m.method}>
                                                                                <td>{m.method}</td>
                                                                                <td style='text-align: right;' class='text-danger'>-{util.format(m.lost, 2)}</td>
                                                                                <td style='text-align: right;' class='text-success'>+{util.format(m.gained, 2)}</td>
                                                                                <td style='text-align: right;' class={m.netEffect > 0 ? 'text-danger' : 'text-success'}>
                                                                                    {m.netEffect > 0 ? '+' : ''}{util.format(m.netEffect, 2)}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {!cashflowAnalysis.increasesActual?.paymentsFromPreviousPeriods?.count &&
                                                 !cashflowAnalysis.increasesActual?.paymentsForInvoiceSales?.count &&
                                                 !cashflowAnalysis.decreasesActual?.unpaidCashFlowSales?.count &&
                                                 !cashflowAnalysis.paymentMethodMismatch?.count && (
                                                    <div class='text-muted small'>
                                                        <Text id='dyrejournal.report.no-sources-found'>No significant sources of difference found.</Text>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div class='text-muted small'>
                                                <Text id='dyrejournal.report.analysis-load-error'>Unable to load analysis data.</Text>
                                            </div>
                                        )}
                                    </div>}

                                    {/* Write-offs Results */}
                                    {showWriteoffs && <div class='mt-3 p-3 border rounded bg-light'>
                                        <div class='d-flex justify-content-between align-items-center mb-3'>
                                            <h6 class='mb-0 border-bottom pb-2 flex-grow-1'>
                                                <i class='fa-duotone fa-file-slash me-2' />
                                                <Text id='dyrejournal.report.writeoffs-title'>Write-offs (Tapsføringer)</Text>
                                            </h6>
                                            <button type='button' class='btn btn-sm btn-link text-muted' onClick={this.onClickCloseWriteoffs}>
                                                <i class='fa-solid fa-times' />
                                            </button>
                                        </div>

                                        {writeoffsLoading ? (
                                            <div class='text-center py-3'>
                                                <i class='fa-duotone fa-spinner fa-spin me-2' />
                                                <Text id='dyrejournal.report.loading'>Loading...</Text>
                                            </div>
                                        ) : writeoffs && writeoffs.length > 0 ? (
                                            <>
                                                {/* Summary */}
                                                <div class='alert alert-info small mb-3'>
                                                    <strong><Text id='dyrejournal.report.writeoffs-summary'>Summary</Text>:</strong>
                                                    <span class='ms-2'>
                                                        {writeoffsSummary?.count || 0} <Text id='dyrejournal.report.writeoffs-count'>write-offs</Text>,
                                                        <strong class='ms-1'>{util.format(writeoffsSummary?.totalAmount || 0, 2)} kr</strong>
                                                    </span>
                                                </div>

                                                {/* Write-offs list */}
                                                <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                    <thead>
                                                        <tr>
                                                            <th><Text id='dyrejournal.report.sale-number'>Sale #</Text></th>
                                                            <th><Text id='dyrejournal.report.writeoff-date'>Write-off Date</Text></th>
                                                            <th><Text id='dyrejournal.report.sale-date'>Sale Date</Text></th>
                                                            <th><Text id='dyrejournal.report.visitor'>Visitor</Text></th>
                                                            <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {writeoffs.map(wo => {
                                                            let lineTotal = 0;
                                                            if (wo.products && Array.isArray(wo.products)) {
                                                                wo.products.forEach(p => {
                                                                    const qty = parseFloat(p.qty) || 0;
                                                                    const price = parseFloat(p.price) || 0;
                                                                    const vat = parseFloat(p.vat) || 0;
                                                                    let lt = qty * price;
                                                                    if (p.discount) lt -= lt * (p.discount / 100);
                                                                    lt = lt * (1 + vat / 100);
                                                                    if (p.discountAmount) lt -= (p.discountAmount * qty);
                                                                    lineTotal += lt;
                                                                });
                                                            }
                                                            return (
                                                                <tr key={wo.id}>
                                                                    <td>
                                                                        <a href={`/dyrejournal-sale/${wo.id}`} class='text-primary' onClick={e => this.onClickGotoSale(e, wo.id)}>
                                                                            {wo.saleNumber || wo.id}
                                                                        </a>
                                                                    </td>
                                                                    <td>{wo.writeOffDate ? util.formatDate(wo.writeOffDate, { locale: 'nb-NO' }) : '-'}</td>
                                                                    <td>{wo.saleDate ? util.formatDate(wo.saleDate, { locale: 'nb-NO' }) : '-'}</td>
                                                                    <td>
                                                                        {wo.visitorInfo ? (
                                                                            <span>{wo.visitorInfo.firstname} {wo.visitorInfo.lastname}</span>
                                                                        ) : '-'}
                                                                    </td>
                                                                    <td style='text-align: right;'>{util.format(lineTotal, 2)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr class='fw-bold'>
                                                            <td colspan='4'><Text id='dyrejournal.report.total'>Total</Text></td>
                                                            <td style='text-align: right;'>{util.format(writeoffsSummary?.totalAmount || 0, 2)}</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </>
                                        ) : (
                                            <div class='text-muted small'>
                                                <Text id='dyrejournal.report.no-writeoffs'>No write-offs found for this period.</Text>
                                            </div>
                                        )}
                                    </div>}
                                </>}

                                {view.report === 'z-report' && <>
                                    <DyrejournalReportSalesAndPayments
                                        saleReport={saleReport}
                                        reportType={view.report}
                                        showCreditCard={showCreditCard}
                                        showConvene={showConvene}
                                        editable
                                        inputFor={this.inputFor}
                                        cardBreakdown={cardBreakdown}
                                    />
                                </>}

                                {(view.report === 'month' || view.report === 'year') && <>
                                    <DyrejournalReportSalesAndPayments
                                        saleReport={saleReport}
                                        reportType={view.report}
                                        showCreditCard={showCreditCard}
                                        showConvene={showConvene}
                                        editable
                                        inputFor={this.inputFor}
                                        cardBreakdown={cardBreakdown}
                                        onAnalyze={this.onClickShowCashflowAnalysis}
                                        cashflowAnalysis={cashflowAnalysis}
                                        cashflowAnalysisLoading={cashflowAnalysisLoading}
                                    />
                                    {/* Card Analysis (NETS) */}
                                    <div class='mt-3'>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-secondary'
                                            onClick={this.onClickShowCardBreakdown}
                                        >
                                            <i class='fa-duotone fa-credit-card me-2' />
                                            <Text id='dyrejournal.report.analyze-card-payments'>Analyze card payments (NETS)</Text>
                                            <span class='badge bg-info text-dark ms-2'><Text id='dyrejournal.report.experimental'>Experimental</Text></span>
                                        </button>
                                    </div>
                                    {showCardBreakdown && <div class='mt-3 p-3 border rounded bg-body-secondary'>
                                        <div class='d-flex justify-content-between align-items-center mb-3'>
                                            <h6 class='mb-0 border-bottom pb-2 flex-grow-1'>
                                                <i class='fa-duotone fa-credit-card me-2' />
                                                <Text id='dyrejournal.report.card-breakdown'>Card breakdown (NETS)</Text>
                                            </h6>
                                            <button type='button' class='btn btn-sm btn-link text-body-secondary' onClick={this.onClickCloseCardBreakdown}>
                                                <i class='fa-solid fa-times' />
                                            </button>
                                        </div>
                                        {cardBreakdownLoading ? (
                                            <div class='text-center py-3'>
                                                <i class='fa-duotone fa-spinner fa-spin me-2' />
                                                <Text id='dyrejournal.report.loading'>Loading...</Text>
                                            </div>
                                        ) : cardBreakdown ? (
                                            <>
                                                <div class='small text-body-secondary mb-2'>
                                                    <Text id='dyrejournal.report.card-breakdown-help'>Based on NETS terminal responses. Only approved transactions are counted. Amounts use the terminal's reported totalAmount.</Text>
                                                </div>

                                                <div class='table-responsive mt-3'>
                                                    <h6 class='mb-2 border-bottom'><Text id='dyrejournal.report.card-category'>By category</Text></h6>
                                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em;'>
                                                        <thead>
                                                            <tr>
                                                                <th><Text id='dyrejournal.report.category'>Category</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.share'>Share</Text></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {cardBreakdown.byCategory?.map(c => (
                                                                <tr>
                                                                    <td>
                                                                        {c.category === 'debit-bankaxept' && <Text id='dyrejournal.report.debit-bankaxept'>BankAxept (debit)</Text>}
                                                                        {c.category === 'debit-card' && <Text id='dyrejournal.report.debit-card'>Debit card (Visa/Mastercard)</Text>}
                                                                        {c.category === 'credit-card' && <Text id='dyrejournal.report.credit-card'>Credit card</Text>}
                                                                        {c.category === 'unknown' && <Text id='dyrejournal.report.unknown'>Unknown</Text>}
                                                                    </td>
                                                                    <td style='text-align: right;'>{c.count}</td>
                                                                    <td style='text-align: right;'><nobr>{util.format(c.amount, 2)}</nobr></td>
                                                                    <td style='text-align: right;'>
                                                                        {cardBreakdown.totalAmount > 0
                                                                            ? `${((c.amount / cardBreakdown.totalAmount) * 100).toFixed(1)}%`
                                                                            : '-'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            <tr style='border-top: 1px solid #808080;'>
                                                                <td><strong><Text id='dyrejournal.report.total'>Total</Text></strong></td>
                                                                <td style='text-align: right;'><strong>{cardBreakdown.totalTransactions}</strong></td>
                                                                <td style='text-align: right;'><strong><nobr>{util.format(cardBreakdown.totalAmount, 2)}</nobr></strong></td>
                                                                <td style='text-align: right;'><strong>100%</strong></td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <div class='table-responsive mt-4'>
                                                    <h6 class='mb-2 border-bottom'><Text id='dyrejournal.report.card-issuer'>By issuer bank</Text></h6>
                                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em;'>
                                                        <thead>
                                                            <tr>
                                                                <th><Text id='dyrejournal.report.issuer'>Issuer</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                <th style='text-align: right;'><Text id='dyrejournal.report.share'>Share</Text></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {cardBreakdown.byIssuer?.map(row => (
                                                                <tr>
                                                                    <td>{row.issuer}</td>
                                                                    <td style='text-align: right;'>{row.count}</td>
                                                                    <td style='text-align: right;'><nobr>{util.format(row.amount, 2)}</nobr></td>
                                                                    <td style='text-align: right;'>
                                                                        {cardBreakdown.totalAmount > 0
                                                                            ? `${((row.amount / cardBreakdown.totalAmount) * 100).toFixed(1)}%`
                                                                            : '-'}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {cardBreakdown.unknownByCardType?.length > 0 && (
                                                    <div class='table-responsive mt-4'>
                                                        <h6 class='mb-2 border-bottom'>
                                                            <Text id='dyrejournal.report.unknown-by-cardtype'>Unclassified by card type (normalized)</Text>
                                                        </h6>
                                                        <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.cardtype'>Card type</Text></th>
                                                                    <th><Text id='dyrejournal.report.scheme'>Scheme</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cardBreakdown.unknownByCardType.map(row => (
                                                                    <tr>
                                                                        <td><code>{row.cardType}</code></td>
                                                                        <td>{row.scheme}</td>
                                                                        <td style='text-align: right;'>{row.count}</td>
                                                                        <td style='text-align: right;'><nobr>{util.format(row.amount, 2)}</nobr></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {cardBreakdown.unknownBins?.length > 0 && (
                                                    <div class='table-responsive mt-4'>
                                                        <h6 class='mb-2 border-bottom'>
                                                            <Text id='dyrejournal.report.unknown-bins'>Unclassified BINs</Text>
                                                        </h6>
                                                        <div class='alert alert-info small mb-2'>
                                                            <i class='fa-duotone fa-circle-info me-2' />
                                                            <Text id='dyrejournal.report.unknown-bins-help'>
                                                                These card transactions could not be automatically classified as debit or credit because the terminal only reported a generic card type (e.g. "Mastercard" or "VISA") without a debit/credit hint. Each row shows the first 6 digits of the card number (BIN — Bank Identification Number), which identifies the issuing bank and card product. They still count toward totals and the issuer breakdown above, but appear under the "Unknown" category. To classify them, add the BIN to BIN_TABLE in backend/lib/card-classifier.js with the issuer name and type ('debit' or 'credit'). BIN info can be looked up on services like binlist.net or bincheck.io.
                                                            </Text>
                                                        </div>
                                                        <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.bin'>BIN</Text></th>
                                                                    <th><Text id='dyrejournal.report.cardtype'>Card type</Text></th>
                                                                    <th><Text id='dyrejournal.report.scheme'>Scheme</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cardBreakdown.unknownBins.map(row => (
                                                                    <tr>
                                                                        <td><code>{row.bin}</code></td>
                                                                        <td>{row.cardType}</td>
                                                                        <td>{row.scheme}</td>
                                                                        <td style='text-align: right;'>{row.count}</td>
                                                                        <td style='text-align: right;'><nobr>{util.format(row.amount, 2)}</nobr></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div class='text-center py-3 text-body-secondary'>
                                                <Text id='dyrejournal.report.no-card-data'>No NETS transactions found for this period.</Text>
                                            </div>
                                        )}
                                    </div>}

                                    {/* Cash Flow Analysis Button */}
                                    <div class='mt-3'>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-secondary'
                                            onClick={this.onClickShowWriteoffs}
                                        >
                                            <i class='fa-duotone fa-file-slash me-2' />
                                            <Text id='dyrejournal.report.show-writeoffs'>Write-offs (Tapsføringer)</Text>
                                        </button>
                                    </div>

                                    {/* Cash Flow Analysis Results */}
                                    {showCashflowAnalysis && <div class='mt-3 p-3 border rounded bg-light'>
                                        <div class='d-flex justify-content-between align-items-center mb-3'>
                                            <h6 class='mb-0 border-bottom pb-2 flex-grow-1'>
                                                <i class='fa-duotone fa-chart-line-up me-2' />
                                                <Text id='dyrejournal.report.cashflow-analysis'>Cash Flow Analysis</Text>
                                                <span class='badge bg-warning text-dark ms-2'><Text id='dyrejournal.report.experimental'>Experimental</Text></span>
                                            </h6>
                                            <button type='button' class='btn btn-sm btn-link text-muted' onClick={this.onClickCloseCashflowAnalysis}>
                                                <i class='fa-solid fa-times' />
                                            </button>
                                        </div>
                                        <div class='alert alert-warning small mb-3'>
                                            <i class='fa-duotone fa-triangle-exclamation me-2' />
                                            <Text id='dyrejournal.report.experimental-note'>This feature is experimental and may not account for 100% of the difference. Use with caution.</Text>
                                        </div>

                                        {cashflowAnalysisLoading ? (
                                            <div class='text-center py-3'>
                                                <i class='fa-duotone fa-spinner fa-spin me-2' />
                                                <Text id='dyrejournal.report.loading'>Loading...</Text>
                                            </div>
                                        ) : cashflowAnalysis ? (
                                            <>
                                                {/* Payments from previous periods */}
                                                {cashflowAnalysis.increasesActual?.paymentsFromPreviousPeriods?.count > 0 && (
                                                    <div class='mb-3'>
                                                        <h6 class='small text-muted mb-2'>
                                                            <i class='fa-duotone fa-clock-rotate-left me-2' />
                                                            <Text id='dyrejournal.report.payments-from-previous'>Payments from Previous Periods</Text>
                                                            <span class='badge bg-success ms-2'>{cashflowAnalysis.increasesActual.paymentsFromPreviousPeriods.count}</span>
                                                        </h6>
                                                        <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.sale-period'>Sale Period</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cashflowAnalysis.increasesActual.paymentsFromPreviousPeriods.byMonth?.map(m => (
                                                                    <>
                                                                        <tr key={m.month}
                                                                            style='cursor: pointer;'
                                                                            onClick={() => this.toggleCashflowSection(`prev-${m.month}`)}
                                                                        >
                                                                            <td>
                                                                                <i class={`fa-duotone ${expandedCashflowSections[`prev-${m.month}`] ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style='font-size: 0.8em;' />
                                                                                {m.month}
                                                                            </td>
                                                                            <td style='text-align: right;'>{m.count}</td>
                                                                            <td style='text-align: right;'>{util.format(m.total, 2)}</td>
                                                                        </tr>
                                                                        {expandedCashflowSections[`prev-${m.month}`] && m.sales?.map(s => (
                                                                            <tr key={s.paymentId} class='text-body-secondary' style='font-size: 0.9em;'>
                                                                                <td class='ps-4'>
                                                                                    <a href={`/dyrejournal-sale/${s.saleId}`} onClick={e => this.onClickGotoSale(e, s.saleId)}>
                                                                                        #{s.saleNumber}
                                                                                    </a>
                                                                                    {s.visitorName ? <span class='ms-2'>{s.visitorName}</span> : null}
                                                                                    <small class='text-body-tertiary ms-2'>{util.isoDate(s.saleDate, false, false, true)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>
                                                                                    <small>{saleStore.getPaymentMethodText(s.paymentMethod)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>{util.format(s.paymentAmount, 2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </>
                                                                ))}
                                                                <tr class='fw-bold'>
                                                                    <td><Text id='dyrejournal.report.total'>Total</Text></td>
                                                                    <td style='text-align: right;'>{cashflowAnalysis.increasesActual.paymentsFromPreviousPeriods.count}</td>
                                                                    <td style='text-align: right;'>{util.format(cashflowAnalysis.increasesActual.paymentsFromPreviousPeriods.total, 2)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {/* Payments for invoice/partial sales */}
                                                {cashflowAnalysis.increasesActual?.paymentsForInvoiceSales?.count > 0 && (
                                                    <div class='mb-3'>
                                                        <h6 class='small text-muted mb-2'>
                                                            <i class='fa-duotone fa-file-invoice me-2' />
                                                            <Text id='dyrejournal.report.payments-for-invoice-sales'>Payments for Invoice/Partial Sales</Text>
                                                            <span class='badge bg-success ms-2'>{cashflowAnalysis.increasesActual.paymentsForInvoiceSales.count}</span>
                                                        </h6>
                                                        <p class='small text-muted mb-2'>
                                                            <Text id='dyrejournal.report.invoice-payments-explanation'>
                                                                Sales marked as invoice/partial, but paid with cash/card/vipps/account
                                                            </Text>
                                                        </p>
                                                        <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.sale-payment-method'>Sale Payment Method</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cashflowAnalysis.increasesActual.paymentsForInvoiceSales.byMethod?.map(m => (
                                                                    <>
                                                                        <tr key={m.salePaymentMethod}
                                                                            style='cursor: pointer;'
                                                                            onClick={() => this.toggleCashflowSection(`inv-${m.salePaymentMethod}`)}
                                                                        >
                                                                            <td>
                                                                                <i class={`fa-duotone ${expandedCashflowSections[`inv-${m.salePaymentMethod}`] ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style='font-size: 0.8em;' />
                                                                                {saleStore.getPaymentMethodText(m.salePaymentMethod)}
                                                                            </td>
                                                                            <td style='text-align: right;'>{m.count}</td>
                                                                            <td style='text-align: right;'>{util.format(m.total, 2)}</td>
                                                                        </tr>
                                                                        {expandedCashflowSections[`inv-${m.salePaymentMethod}`] && m.sales?.map(s => (
                                                                            <tr key={s.paymentId} class='text-body-secondary' style='font-size: 0.9em;'>
                                                                                <td class='ps-4'>
                                                                                    <a href={`/dyrejournal-sale/${s.saleId}`} onClick={e => this.onClickGotoSale(e, s.saleId)}>
                                                                                        #{s.saleNumber}
                                                                                    </a>
                                                                                    {s.visitorName ? <span class='ms-2'>{s.visitorName}</span> : null}
                                                                                    <small class='text-body-tertiary ms-2'>{util.isoDate(s.saleDate, false, false, true)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>
                                                                                    <small>{saleStore.getPaymentMethodText(s.paymentMethod)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>{util.format(s.paymentAmount, 2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </>
                                                                ))}
                                                                <tr class='fw-bold'>
                                                                    <td><Text id='dyrejournal.report.total'>Total</Text></td>
                                                                    <td style='text-align: right;'>{cashflowAnalysis.increasesActual.paymentsForInvoiceSales.count}</td>
                                                                    <td style='text-align: right;'>{util.format(cashflowAnalysis.increasesActual.paymentsForInvoiceSales.total, 2)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {/* Unpaid cash flow sales */}
                                                {cashflowAnalysis.decreasesActual?.unpaidCashFlowSales?.count > 0 && (
                                                    <div class='mb-3'>
                                                        <h6 class='small text-muted mb-2'>
                                                            <i class='fa-duotone fa-hourglass-half me-2' />
                                                            <Text id='dyrejournal.report.unpaid-cashflow-sales-title'>Unpaid Sales (marked cash/card/vipps/account)</Text>
                                                            <span class='badge bg-warning text-dark ms-2'>{cashflowAnalysis.decreasesActual.unpaidCashFlowSales.count}</span>
                                                        </h6>
                                                        <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.payment-method'>Payment Method</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.unpaid-amount'>Unpaid Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cashflowAnalysis.decreasesActual.unpaidCashFlowSales.byMethod?.map(m => (
                                                                    <>
                                                                        <tr key={m.paymentMethod}
                                                                            style='cursor: pointer;'
                                                                            onClick={() => this.toggleCashflowSection(`unpaid-${m.paymentMethod}`)}
                                                                        >
                                                                            <td>
                                                                                <i class={`fa-duotone ${expandedCashflowSections[`unpaid-${m.paymentMethod}`] ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style='font-size: 0.8em;' />
                                                                                {saleStore.getPaymentMethodText(m.paymentMethod)}
                                                                            </td>
                                                                            <td style='text-align: right;'>{m.count}</td>
                                                                            <td style='text-align: right;'>{util.format(m.total, 2)}</td>
                                                                        </tr>
                                                                        {expandedCashflowSections[`unpaid-${m.paymentMethod}`] && m.sales?.map(s => (
                                                                            <tr key={s.saleId} class='text-body-secondary' style='font-size: 0.9em;'>
                                                                                <td class='ps-4'>
                                                                                    <a href={`/dyrejournal-sale/${s.saleId}`} onClick={e => this.onClickGotoSale(e, s.saleId)}>
                                                                                        #{s.saleNumber}
                                                                                    </a>
                                                                                    {s.visitorName ? <span class='ms-2'>{s.visitorName}</span> : null}
                                                                                    <small class='text-body-tertiary ms-2'>{util.isoDate(s.saleDate, false, false, true)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>
                                                                                    <small>
                                                                                        <Text id='dyrejournal.report.paid'>Paid</Text>: {util.format(s.totalPaid || 0, 2)}
                                                                                        {' / '}
                                                                                        {util.format(s.saleTotal || 0, 2)}
                                                                                    </small>
                                                                                </td>
                                                                                <td style='text-align: right;'>{util.format(s.unpaidAmount, 2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </>
                                                                ))}
                                                                <tr class='fw-bold'>
                                                                    <td><Text id='dyrejournal.report.total'>Total</Text></td>
                                                                    <td style='text-align: right;'>{cashflowAnalysis.decreasesActual.unpaidCashFlowSales.count}</td>
                                                                    <td style='text-align: right;'>{util.format(cashflowAnalysis.decreasesActual.unpaidCashFlowSales.total, 2)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}

                                                {/* Payment method mismatches */}
                                                {cashflowAnalysis.paymentMethodMismatch?.count > 0 && (
                                                    <div class='mb-3'>
                                                        <h6 class='small text-muted mb-2'>
                                                            <i class='fa-duotone fa-shuffle me-2' />
                                                            <Text id='dyrejournal.report.payment-method-mismatch-title'>Payment Method Mismatch</Text>
                                                            <span class='badge bg-info ms-2'>{cashflowAnalysis.paymentMethodMismatch.count}</span>
                                                        </h6>
                                                        <p class='small text-muted mb-2'>
                                                            <Text id='dyrejournal.report.payment-method-mismatch-desc'>Sales marked with one payment method but paid with a different method. This moves money between methods without affecting the total.</Text>
                                                        </p>
                                                        <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                            <thead>
                                                                <tr>
                                                                    <th><Text id='dyrejournal.report.from-method'>From Method</Text></th>
                                                                    <th><Text id='dyrejournal.report.to-method'>To Method</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.count'>Count</Text></th>
                                                                    <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {cashflowAnalysis.paymentMethodMismatch.byFromTo?.map(m => (
                                                                    <>
                                                                        <tr key={`${m.fromMethod}-${m.toMethod}`}
                                                                            style='cursor: pointer;'
                                                                            onClick={() => this.toggleCashflowSection(`mm-${m.fromMethod}-${m.toMethod}`)}
                                                                        >
                                                                            <td>
                                                                                <i class={`fa-duotone ${expandedCashflowSections[`mm-${m.fromMethod}-${m.toMethod}`] ? 'fa-chevron-down' : 'fa-chevron-right'} me-1`} style='font-size: 0.8em;' />
                                                                                {saleStore.getPaymentMethodText(m.fromMethod)}
                                                                            </td>
                                                                            <td>{saleStore.getPaymentMethodText(m.toMethod)}</td>
                                                                            <td style='text-align: right;'>{m.count}</td>
                                                                            <td style='text-align: right;'>{util.format(m.total, 2)}</td>
                                                                        </tr>
                                                                        {expandedCashflowSections[`mm-${m.fromMethod}-${m.toMethod}`] && m.sales?.map(s => (
                                                                            <tr key={s.paymentId} class='text-body-secondary' style='font-size: 0.9em;'>
                                                                                <td class='ps-4' colspan='2'>
                                                                                    <a href={`/dyrejournal-sale/${s.saleId}`} onClick={e => this.onClickGotoSale(e, s.saleId)}>
                                                                                        #{s.saleNumber}
                                                                                    </a>
                                                                                    {s.visitorName ? <span class='ms-2'>{s.visitorName}</span> : null}
                                                                                    <small class='text-body-tertiary ms-2'>{util.isoDate(s.saleDate, false, false, true)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>
                                                                                    <small>{saleStore.getPaymentMethodText(s.paymentMethod)}</small>
                                                                                </td>
                                                                                <td style='text-align: right;'>{util.format(s.paymentAmount, 2)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        {cashflowAnalysis.paymentMethodMismatch.perMethod?.length > 0 && (
                                                            <div class='mt-2'>
                                                                <p class='small text-muted mb-1'><Text id='dyrejournal.report.net-effect-per-method'>Net effect per method:</Text></p>
                                                                <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                                    <thead>
                                                                        <tr>
                                                                            <th><Text id='dyrejournal.report.method'>Method</Text></th>
                                                                            <th style='text-align: right;'><Text id='dyrejournal.report.lost'>Lost (from expected)</Text></th>
                                                                            <th style='text-align: right;'><Text id='dyrejournal.report.gained'>Gained (to actual)</Text></th>
                                                                            <th style='text-align: right;'><Text id='dyrejournal.report.net-effect'>Net Effect</Text></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {cashflowAnalysis.paymentMethodMismatch.perMethod?.map(m => (
                                                                            <tr key={m.method}>
                                                                                <td>{m.method}</td>
                                                                                <td style='text-align: right;' class='text-danger'>-{util.format(m.lost, 2)}</td>
                                                                                <td style='text-align: right;' class='text-success'>+{util.format(m.gained, 2)}</td>
                                                                                <td style='text-align: right;' class={m.netEffect > 0 ? 'text-danger' : 'text-success'}>
                                                                                    {m.netEffect > 0 ? '+' : ''}{util.format(m.netEffect, 2)}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {!cashflowAnalysis.increasesActual?.paymentsFromPreviousPeriods?.count &&
                                                 !cashflowAnalysis.increasesActual?.paymentsForInvoiceSales?.count &&
                                                 !cashflowAnalysis.decreasesActual?.unpaidCashFlowSales?.count &&
                                                 !cashflowAnalysis.paymentMethodMismatch?.count && (
                                                    <div class='text-muted small'>
                                                        <Text id='dyrejournal.report.no-sources-found'>No significant sources of difference found.</Text>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div class='text-muted small'>
                                                <Text id='dyrejournal.report.analysis-load-error'>Unable to load analysis data.</Text>
                                            </div>
                                        )}
                                    </div>}

                                    {/* Write-offs Results */}
                                    {showWriteoffs && <div class='mt-3 p-3 border rounded bg-light'>
                                        <div class='d-flex justify-content-between align-items-center mb-3'>
                                            <h6 class='mb-0 border-bottom pb-2 flex-grow-1'>
                                                <i class='fa-duotone fa-file-slash me-2' />
                                                <Text id='dyrejournal.report.writeoffs-title'>Write-offs (Tapsføringer)</Text>
                                            </h6>
                                            <button type='button' class='btn btn-sm btn-link text-muted' onClick={this.onClickCloseWriteoffs}>
                                                <i class='fa-solid fa-times' />
                                            </button>
                                        </div>

                                        {writeoffsLoading ? (
                                            <div class='text-center py-3'>
                                                <i class='fa-duotone fa-spinner fa-spin me-2' />
                                                <Text id='dyrejournal.report.loading'>Loading...</Text>
                                            </div>
                                        ) : writeoffs && writeoffs.length > 0 ? (
                                            <>
                                                {/* Summary */}
                                                <div class='alert alert-info small mb-3'>
                                                    <strong><Text id='dyrejournal.report.writeoffs-summary'>Summary</Text>:</strong>
                                                    <span class='ms-2'>
                                                        {writeoffsSummary?.count || 0} <Text id='dyrejournal.report.writeoffs-count'>write-offs</Text>,
                                                        <strong class='ms-1'>{util.format(writeoffsSummary?.totalAmount || 0, 2)} kr</strong>
                                                    </span>
                                                </div>

                                                {/* Write-offs list */}
                                                <table class='table table-sm table-striped font-style-narrow' style='font-size: 0.85em;'>
                                                    <thead>
                                                        <tr>
                                                            <th><Text id='dyrejournal.report.sale-number'>Sale #</Text></th>
                                                            <th><Text id='dyrejournal.report.writeoff-date'>Write-off Date</Text></th>
                                                            <th><Text id='dyrejournal.report.sale-date'>Sale Date</Text></th>
                                                            <th><Text id='dyrejournal.report.visitor'>Visitor</Text></th>
                                                            <th style='text-align: right;'><Text id='dyrejournal.report.amount'>Amount</Text></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {writeoffs.map(wo => {
                                                            let lineTotal = 0;
                                                            if (wo.products && Array.isArray(wo.products)) {
                                                                wo.products.forEach(p => {
                                                                    const qty = parseFloat(p.qty) || 0;
                                                                    const price = parseFloat(p.price) || 0;
                                                                    const vat = parseFloat(p.vat) || 0;
                                                                    let lt = qty * price;
                                                                    if (p.discount) lt -= lt * (p.discount / 100);
                                                                    lt = lt * (1 + vat / 100);
                                                                    if (p.discountAmount) lt -= (p.discountAmount * qty);
                                                                    lineTotal += lt;
                                                                });
                                                            }
                                                            return (
                                                                <tr key={wo.id}>
                                                                    <td>
                                                                        <a href={`/dyrejournal-sale/${wo.id}`} class='text-primary' onClick={e => this.onClickGotoSale(e, wo.id)}>
                                                                            {wo.saleNumber || wo.id}
                                                                        </a>
                                                                    </td>
                                                                    <td>{wo.writeOffDate ? util.formatDate(wo.writeOffDate, { locale: 'nb-NO' }) : '-'}</td>
                                                                    <td>{wo.saleDate ? util.formatDate(wo.saleDate, { locale: 'nb-NO' }) : '-'}</td>
                                                                    <td>
                                                                        {wo.visitorInfo ? (
                                                                            <span>{wo.visitorInfo.firstname} {wo.visitorInfo.lastname}</span>
                                                                        ) : '-'}
                                                                    </td>
                                                                    <td style='text-align: right;'>{util.format(lineTotal, 2)}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr class='fw-bold'>
                                                            <td colspan='4'><Text id='dyrejournal.report.total'>Total</Text></td>
                                                            <td style='text-align: right;'>{util.format(writeoffsSummary?.totalAmount || 0, 2)}</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </>
                                        ) : (
                                            <div class='text-muted small'>
                                                <Text id='dyrejournal.report.no-writeoffs'>No write-offs found for this period.</Text>
                                            </div>
                                        )}
                                    </div>}
                                </>}

                                <div class='w-100 d-flex flex-row justify-content-center'>
                                    {/* <button type='button' class='btn btn-primary rounded-pill mx-2' onClick={this.onClickPrintReport}>
                                        <i class='fa-solid fa-print' /> <Text id='dyrejournal.report.print'>Print report</Text>
                                    </button> */}
                                    <button
                                        type='button'
                                        class={`btn btn-danger rounded-pill mx-2`}
                                        onClick={this.onClickSaveReport}
                                        disabled={saveReportInserting || loading || !!reportStatus}
                                    >
                                        {view.report === 'z-report' ? <>
                                            <i class='fa-solid fa-floppy-disk' /> <Text id='dyrejournal.report.create-z-report'>Create z-report</Text>
                                        </> : <>
                                            {view.report === 'periode' ? <>
                                                <i class='fa-solid fa-floppy-disk' /> <Text id='dyrejournal.report.create-salereport'>Create sale report</Text> - {util.isoDate(reportDateTimeStart, true, false)} - {util.isoDate(reportDateTimeEnd, true, false)}
                                            </> : <>
                                                <i class='fa-solid fa-floppy-disk' /> <Text id='dyrejournal.report.create-salereport'>Create sale report</Text> - {getReportDate(reportDate, view.report, onlyFromDate)}
                                            </>}
                                        </>}

                                        {saveReportInserting && <i class='fa-solid fa-spinner-third fa-spin' />}
                                    </button>
                                    {this.state.saveReportError && <div class='alert alert-danger mt-2' role='alert'>
                                        <Text id='dyrejournal.report.save-report-error'>
                                            The report was not saved. Reload the report, check the cash amounts
                                            and try again.
                                        </Text>
                                    </div>}
                                </div>

{/* <xmp>{JSON.stringify(saleReport, null, 4)}</xmp> */}
{/* <xmp>{JSON.stringify(saleReports, null, 4)}</xmp> */}

                                {/* <div class='table-responsive w-50'>
                                    <table class='table table-sm table-hover table-striped font-style-narrow'>
                                        <tbody>
                                            <tr>
                                                <th><Text id='dyrejournal.report.total-sales'>Total sales</Text></th>
                                                <td style='text-align: right;'>{totalNumberOfSales}</td>
                                            </tr>
                                            <tr>
                                                <th><Text id='dyrejournal.report.total-amount-wo-tax'>Total amount wo/tax</Text></th>
                                                <td style='text-align: right;'>{util.format(saleStore.lineTotal(aggSaleTotal, true, 1), 2)}</td>
                                            </tr>
                                            <tr>
                                                <th><Text id='dyrejournal.report.total-tax'>Total tax</Text></th>
                                                <td style='text-align: right;'>{util.format(aggSaleTotal.vat, 2)}</td>
                                            </tr>
                                            <tr>
                                                <th><Text id='dyrejournal.report.total-number-of-products-services'>Total number of products/services</Text></th>
                                                <td style='text-align: right;'>{aggSaleTotal.count}</td>
                                            </tr>
                                            {aggSaleTotalPaymentMethods && aggSaleTotalPaymentMethods.map((item, idx) => {
                                                return (<>
                                                    <tr>
                                                        <th><Text id='dyrejournal.report.total-amount-wo-tax'>Total amount wo/tax for</Text> {item.paymentMethod}</th>
                                                        <td style='text-align: right;'>{util.format(item.total, 2)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th><Text id='dyrejournal.report.total-tax'>Total tax for</Text> {item.paymentMethod}</th>
                                                        <td style='text-align: right;'>{util.format(item.tax, 2)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th><Text id='dyrejournal.report.total-number-of-products-services'>Total number of products/services for</Text> {item.paymentMethod}</th>
                                                        <td style='text-align: right;'>{item.count}</td>
                                                    </tr>
                                                </>);
                                            })}
                                        </tbody>
                                    </table>
                                </div> */}

                                {/* Report for today<br />
                                Total number of sales: {totalNumberOfSales}<br />
                                Total amount wo/tax: {util.format(aggSaleTotal.total, 2)}<br />
                                Total tax: {util.format(aggSaleTotal.tax, 2)}<br />
                                Total number of products/services: {aggSaleTotal.count}<br />

                                {aggSaleTotalPaymentMethods && aggSaleTotalPaymentMethods.map((item, idx) => {
                                    return (<>
                                        Total amount wo/tax for {item.paymentMethod}: {util.format(item.total, 2)}<br />
                                        Total tax for {item.paymentMethod}: {util.format(item.tax, 2)}<br />
                                        Total number of products/services for {item.paymentMethod}: {item.count}<br />
                                    </>);
                                })} */}
                                {/* Total appointments: 0<br />
                                Total journals: 0<br />
                                Total prescriptions: 0<br />
                                Total animals: 0<br /> */}

                                {/* <xmp>{JSON.stringify(aggSaleTotal, null, 2)}</xmp>
                                <xmp>{JSON.stringify(aggSaleTotalPaymentMethods, null, 2)}</xmp>
                                <xmp>{JSON.stringify(aggSaleTotalTax, null, 2)}</xmp> */}
                                </div>
                            </>}
                        </div>
                    </div>

                    {/* Sales with Discount Section */}
                    {view.report !== 'accounts-yearly' && view.report !== 'accounts-receivable' && aggSalesWithDiscount && aggSalesWithDiscount.length > 0 && <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='d-flex flex-row justify-content-between'>
                                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                    <Text id='dyrejournal.report.sales-with-discount'>Sales with discount</Text>
                                </div>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <div class='table-responsive' style='max-height: 400px;'>
                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em; line-height: 1.0em;'>
                                        <thead>
                                            <tr>
                                                <th><Text id='dyrejournal.report.sale-number'>Sale #</Text></th>
                                                <th><Text id='dyrejournal.report.date'>Date</Text></th>
                                                <th><Text id='dyrejournal.report.customer'>Customer</Text></th>
                                                <th><Text id='dyrejournal.report.employee'>Employee</Text></th>
                                                <th><Text id='dyrejournal.report.product'>Product</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.discount-percent'>Discount %</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.discount-amount'>Discount kr</Text></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {aggSalesWithDiscount.map((sale, saleIdx) => {
                                                const visitor = sale.visitors?.[0];
                                                const visitorName = visitor ? `${visitor.firstname || ''} ${visitor.lastname || ''}`.trim() : '';
                                                const employeeName = (sale.userFirstname || sale.userLastname)
                                                    ? `${sale.userFirstname || ''} ${sale.userLastname || ''}`.trim()
                                                    : `User ${sale.user}`;

                                                return sale.products?.map((product, prodIdx) => {
                                                    const discountPercent = product.discount || 0;
                                                    const discountAmount = product.discountAmount || 0;

                                                    return (
                                                        <tr key={`${saleIdx}-${prodIdx}`}>
                                                            {prodIdx === 0 ? <>
                                                                <td rowspan={sale.products.length}>
                                                                    <a href={`/dyrejournal-sale/${sale.saleNumber}`} onClick={e => this.onClickGotoSale(e, sale.saleNumber)}>
                                                                        {sale.saleNumber}
                                                                    </a>
                                                                </td>
                                                                <td rowspan={sale.products.length}>
                                                                    <nobr>{util.isoDate(sale.saleDate, false, false, true)}</nobr>
                                                                </td>
                                                                <td rowspan={sale.products.length}>{visitorName || '-'}</td>
                                                                <td rowspan={sale.products.length}>{employeeName}</td>
                                                            </> : null}
                                                            <td>{product.name || product.id}</td>
                                                            <td style='text-align: right;'>
                                                                {discountPercent > 0 ? `${util.format(discountPercent, 0)}%` : '-'}
                                                            </td>
                                                            <td style='text-align: right;'>
                                                                {discountAmount > 0 ? util.format(discountAmount, 2) : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr class='font-weight-bold'>
                                                <td colspan='5'><Text id='dyrejournal.report.total'>Total</Text></td>
                                                <td style='text-align: right;'>-</td>
                                                <td style='text-align: right;'>
                                                    {util.format(aggSalesWithDiscount.reduce((sum, sale) =>
                                                        sum + (sale.products?.reduce((pSum, p) => pSum + (p.discountAmount || 0), 0) || 0), 0), 2)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <small class='text-muted mt-2'>
                                    <Text id='dyrejournal.report.sales-with-discount-help'>Overview of all sales with discounts. Shows who received and who gave each discount.</Text>
                                </small>
                            </div>
                        </div>
                    </div>}

                    {view.report !== 'accounts-yearly' && view.report !== 'accounts-receivable' && <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='d-flex flex-row justify-content-between'>
                                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                    <Text id='dyrejournal.report.department-sales'>Sales by department</Text>
                                </div>
                                {/* <div class='d-flex flex-row'>
                                    <a
                                        class='btn btn-sm btn-outline-primary rounded-pill mx-2'
                                        href={`${apiServer}/api/sales/report/user/csv?jwtToken=${jwtToken}&reportDate=${reportDate}&type=${view.report}&onlyFromDate=${onlyFromDate}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >
                                        <i class='fa-duotone fa-download' /> <Text id='dyrejournal.report.download'>Download</Text>
                                    </a>
                                </div> */}
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <div
                                    class='table-responsive'
                                    style={`
                                        max-height: ${extendedUser ? 'unset' : `${USER_HEIGHT}px;`};
                                    `}
                                    ref={el => this.usersContainer = el}
                                >

                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em; line-height: 1.0em;'>
                                        <thead>
                                            <tr>
                                                <th class='w-50'><Text id='dyrejournal.report.department'>Department</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.sales'>Sales</Text></th>
                                                {/* {aggSalePersonsAccounts && aggSalePersonsAccounts.sort().map(account => {
                                                    return (<>
                                                        <th style='text-align: right;'>{getAccountName(account, userStore.customer) || account || 'Ingen konto'}</th>
                                                    </>);
                                                })} */}
                                                <th style='text-align: right;'><Text id='dyrejournal.report.fee'>Fee</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.medicine'>Medicine</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.equipment'>Equipment</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.other'>Other</Text></th>

                                                <th style='text-align: right;'><Text id='dyrejournal.report.price-flat'>Price wo/granularity</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.price-discount'>Price discount</Text></th>

                                                {/* <th style='text-align: right;'><Text id='dyrejournal.report.tax'>Tax inc</Text></th> */}
                                                <th>&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {aggSaleDepartments && aggSaleDepartments.map((departmentSale, idx) => {
                                                const totalSales = departmentSale.accounts?.reduce((acc, item) => acc + item.saleCount, 0);
                                                // All buckets zero = the line has no split, so fall back to price. A
                                                // negative bucket is still a split (credit/correction). TICKET-01499.
                                                const hasOnlyPrice = !(departmentSale.fee || departmentSale.medicine || departmentSale.equipment || departmentSale.other);
                                                const department = customer.departments?.find(item => item.id === departmentSale.department);
                                                return (<>
                                                    <tr>
                                                        <td>
                                                            {department?.name ? `${department.id}:  ${department.name}` : departmentSale.department}
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(totalSales, 0)}
                                                            </nobr>
                                                        </td>
                                                        {/* {aggSalePersonsAccounts && aggSalePersonsAccounts.sort().map(account => {
                                                            const accountSale = userSale.accounts?.find(item => item.account === account);
                                                            return (<>
                                                                <td style='text-align: right;'>
                                                                    <nobr>
                                                                        {accountSale ? <>
                                                                            {util.format(accountSale.price, 2)} <span class='font-weight-lighter'>({accountSale.saleCount})</span>
                                                                        </> : <></>}
                                                                    </nobr>
                                                                </td>
                                                            </>);
                                                        })} */}

                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(departmentSale.fee, 2)}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(departmentSale.medicine, 2)}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(departmentSale.equipment, 2)}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {hasOnlyPrice ? <>
                                                                    {util.format(departmentSale.price, 2)}
                                                                </> : <>
                                                                    {util.format(departmentSale.other, 2)}
                                                                </>}
                                                            </nobr>
                                                        </td>

                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {hasOnlyPrice ? <>
                                                                    {util.format(departmentSale.price, 2)}
                                                                </> : <>
                                                                    {util.format(departmentSale.fee + departmentSale.medicine + departmentSale.equipment + departmentSale.other, 2)}
                                                                </>}
                                                                {/*
                                                                {util.format(departmentSale.fee + departmentSale.medicine + departmentSale.equipment + departmentSale.other, 2)}
                                                                {/* {util.format(userSale.price, 2)} */}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(departmentSale.discountAmount / 1.25, 2)}
                                                            </nobr>
                                                        </td>
                                                        {/* <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(userSale.price / 1.25 * 0.25, 2)}
                                                            </nobr>
                                                        </td> */}
                                                        <td>
                                                            <nobr>
                                                                {/* <a
                                                                    class='btn btn-sm btn-outline-primary rounded-pill py-0'
                                                                    href={`${apiServer}/api/sales/report/user-detail/csv?jwtToken=${jwtToken}&reportDate=${reportDate}&type=${view.report}&onlyFromDate=${onlyFromDate}&userId=${departmentSale.user?.id}&startDate=${view.report === 'periode' ? reportDateTimeStart : ''}&endDate=${view.report === 'periode' ? reportDateTimeEnd : ''}`}
                                                                    target='_blank'
                                                                    rel='noopener noreferrer'
                                                                >
                                                                    <i class='fa-duotone fa-download' /> <Text id='dyrejournal.report.sale-download'>By sales</Text>
                                                                </a> */}
                                                                {/* <a
                                                                    class='btn btn-sm btn-outline-primary rounded-pill py-0 ms-2'
                                                                    href={`${apiServer}/api/sales/report/user-detail-products/csv?jwtToken=${jwtToken}&reportDate=${reportDate}&type=${view.report}&onlyFromDate=${onlyFromDate}&userId=${departmentSale.user?.id}&startDate=${view.report === 'periode' ? reportDateTimeStart : ''}&endDate=${view.report === 'periode' ? reportDateTimeEnd : ''}`}
                                                                    target='_blank'
                                                                    rel='noopener noreferrer'
                                                                >
                                                                    <i class='fa-duotone fa-download' /> <Text id='dyrejournal.report.products-download'>By product lines</Text>
                                                                </a> */}

                                                                {/* <button
                                                                    class='btn btn-sm btn-outline-primary rounded-pill py-0 ms-2'
                                                                    data-id={departmentSale.user?.id}
                                                                    data-date={reportDate}
                                                                    data-datefrom={view.report === 'periode' ? reportDateTimeStart : undefined}
                                                                    data-dateto={view.report === 'periode' ? reportDateTimeEnd : undefined}
                                                                    data-type={view.report}
                                                                    data-onlyfromdate={onlyFromDate}
                                                                    onClick={this.onClickUserDetail}
                                                                >
                                                                    <i class='fa-duotone fa-eye' /> <Text id='dyrejournal.report.sale-download'>By sales</Text>
                                                                </button> */}
                                                                {/* <button
                                                                    class='btn btn-sm btn-outline-primary rounded-pill py-0 ms-2'
                                                                    data-id={departmentSale.user?.id}
                                                                    data-date={reportDate}
                                                                    data-datefrom={view.report === 'periode' ? reportDateTimeStart : undefined}
                                                                    data-dateto={view.report === 'periode' ? reportDateTimeEnd : undefined}
                                                                    data-type={view.report}
                                                                    data-onlyfromdate={onlyFromDate}
                                                                    onClick={this.onClickUserDetailProducts}
                                                                >
                                                                    <i class='fa-duotone fa-eye' /> <Text id='dyrejournal.report.products-download'>By product lines</Text>
                                                                </button> */}
                                                            </nobr>
                                                        </td>
                                                    </tr>
                                                </>);
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <div class='w-100 d-flex justify-content-center'>
                                    {extendedDepartment ? <>
                                        <button
                                            class='btn btn-sm btn-link text-muted p-0'
                                            onClick={this.toggleExtendedDepartment}
                                            >
                                            <Text id='gui.viewbox-less'>Show less</Text> <i class='fa-duotone fa-angle-up' />
                                        </button>
                                    </> : <>
                                        <button
                                            class='btn btn-sm btn-link text-muted p-0'
                                            onClick={this.toggleExtendedDepartment}
                                            >
                                            <Text id='gui.viewbox-more'>Show more</Text> <i class='fa-duotone fa-angle-down' />
                                        </button>
                                    </>}
                                </div>

                            </div>
                            <div class='w-100 d-flex flex-row justify-content-center'>
                                <small class='text-muted'>
                                    <Text id='dyrejournal.report.department-sales-help'>Sales by department. The sum is the total sale for this department without rounding price to nearest NOK. Tax is NOT included in the sum.</Text>
                                </small>
                            </div>
                        </div>
                    </div>}

                    {view.report !== 'accounts-yearly' && view.report !== 'accounts-receivable' && <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='d-flex flex-row justify-content-between'>
                                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                    <Text id='dyrejournal.report.user-sales'>Sales by user</Text>
                                </div>
                                <div class='d-flex flex-row'>
                                    <a
                                        class='btn btn-sm btn-outline-primary rounded-pill mx-2'
                                        href={`${apiServer}/api/sales/report/user/csv?jwtToken=${jwtToken}&reportDate=${reportDate}&type=${view.report}&onlyFromDate=${onlyFromDate}&startDate=${view.report === 'periode' ? reportDateTimeStart : ''}&endDate=${view.report === 'periode' ? reportDateTimeEnd : ''}&reportDepartment=${reportDepartment || ''}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >
                                        <i class='fa-duotone fa-download' /> <Text id='dyrejournal.report.download'>Download</Text>
                                    </a>
                                    <a
                                        class='btn btn-sm btn-outline-primary rounded-pill mx-2'
                                        href={`${apiServer}/api/sales/report/user/csv?jwtToken=${jwtToken}&reportDate=${reportDate}&type=${view.report}&onlyFromDate=${onlyFromDate}&startDate=${view.report === 'periode' ? reportDateTimeStart : ''}&endDate=${view.report === 'periode' ? reportDateTimeEnd : ''}&reportDepartment=${reportDepartment || ''}&format=xlsx`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >
                                        <i class='fa-duotone fa-file-excel' /> <Text id='dyrejournal.report.download-excel'>Download Excel</Text>
                                    </a>
                                </div>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <div
                                    class='table-responsive'
                                    style={`
                                        max-height: ${extendedUser ? 'unset' : `${USER_HEIGHT}px;`};
                                    `}
                                    ref={el => this.usersContainer = el}
                                >

                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em; line-height: 1.0em;'>
                                        <thead>
                                            <tr>
                                                <th class='w-50'><Text id='dyrejournal.report.user'>User</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.sales'>Sales</Text></th>
                                                {/* {aggSalePersonsAccounts && aggSalePersonsAccounts.sort().map(account => {
                                                    return (<>
                                                        <th style='text-align: right;'>{getAccountName(account, userStore.customer) || account || 'Ingen konto'}</th>
                                                    </>);
                                                })} */}
                                                <th style='text-align: right;'><Text id='dyrejournal.report.fee'>Fee</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.medicine'>Medicine</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.equipment'>Equipment</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.other'>Other</Text></th>

                                                <th style='text-align: right;'><Text id='dyrejournal.report.price-flat'>Price wo/granularity</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.price-discount'>Price discount</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.commissionRate'>Rate %</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.commission'>Commission</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.commissionGross'>Commission incl. VAT</Text></th>
                                                <th style='text-align: right;'>&nbsp;</th>

                                                {/* <th style='text-align: right;'><Text id='dyrejournal.report.tax'>Tax inc</Text></th> */}
                                                <th>&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {aggSalePersons && aggSalePersons.map((userSale, idx) => {
                                                const totalSales = userSale.accounts?.reduce((acc, item) => acc + item.saleCount, 0);
                                                // All buckets zero = the line has no split, so fall back to price. A
                                                // negative bucket is still a split (credit/correction). TICKET-01499.
                                                const hasOnlyPrice = !(userSale.fee || userSale.medicine || userSale.equipment || userSale.other);
                                                return (<>
                                                    <tr>
                                                        <td>{userSale.user?.firstname} {userSale.user?.lastname}</td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(totalSales, 0)}
                                                            </nobr>
                                                        </td>
                                                        {/* {aggSalePersonsAccounts && aggSalePersonsAccounts.sort().map(account => {
                                                            const accountSale = userSale.accounts?.find(item => item.account === account);
                                                            return (<>
                                                                <td style='text-align: right;'>
                                                                    <nobr>
                                                                        {accountSale ? <>
                                                                            {util.format(accountSale.price, 2)} <span class='font-weight-lighter'>({accountSale.saleCount})</span>
                                                                        </> : <></>}
                                                                    </nobr>
                                                                </td>
                                                            </>);
                                                        })} */}

                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(userSale.honorar, 2)}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(userSale.medicine, 2)}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(userSale.equipment, 2)}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {hasOnlyPrice ? <>
                                                                    {util.format(userSale.price, 2)}
                                                                </> : <>
                                                                    {util.format(userSale.other, 2)}
                                                                </>}
                                                            </nobr>
                                                        </td>

                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {hasOnlyPrice ? <>
                                                                    {util.format(userSale.price, 2)}
                                                                </> : <>
                                                                    {util.format(userSale.fee + userSale.medicine + userSale.equipment + userSale.other, 2)}
                                                                </>}
                                                                {/* {util.format(userSale.price, 2)} */}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(userSale.discountAmount / 1.25, 2)}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>{userSale.commissionRate > 0 ? `${util.format(userSale.commissionRate, 0)}%` : ''}</nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>{util.format(userSale.commissionNet, 2)}</nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>{util.format(userSale.commissionGross, 2)}</nobr>
                                                        </td>
                                                        {/* <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(userSale.price / 1.25 * 0.25, 2)}
                                                            </nobr>
                                                        </td> */}
                                                        <td>
                                                            <nobr>
                                                                <a
                                                                    class='btn btn-sm btn-outline-primary rounded-pill py-0'
                                                                    href={`${apiServer}/api/sales/report/user-detail/csv?jwtToken=${jwtToken}&reportDate=${reportDate}&type=${view.report}&onlyFromDate=${onlyFromDate}&userId=${userSale.user?.id}&startDate=${view.report === 'periode' ? reportDateTimeStart : ''}&endDate=${view.report === 'periode' ? reportDateTimeEnd : ''}&reportDepartment=${reportDepartment || ''}`}
                                                                    target='_blank'
                                                                    rel='noopener noreferrer'
                                                                >
                                                                    <i class='fa-duotone fa-download' /> <Text id='dyrejournal.report.sale-download'>By sales</Text>
                                                                </a>
                                                                <a
                                                                    class='btn btn-sm btn-outline-primary rounded-pill py-0 ms-2'
                                                                    href={`${apiServer}/api/sales/report/user-detail-products/csv?jwtToken=${jwtToken}&reportDate=${reportDate}&type=${view.report}&onlyFromDate=${onlyFromDate}&userId=${userSale.user?.id}&startDate=${view.report === 'periode' ? reportDateTimeStart : ''}&endDate=${view.report === 'periode' ? reportDateTimeEnd : ''}&reportDepartment=${reportDepartment || ''}`}
                                                                    target='_blank'
                                                                    rel='noopener noreferrer'
                                                                >
                                                                    <i class='fa-duotone fa-download' /> <Text id='dyrejournal.report.products-download'>By product lines</Text>
                                                                </a>

                                                                <button
                                                                    class='btn btn-sm btn-outline-primary rounded-pill py-0 ms-2'
                                                                    data-id={userSale.user?.id}
                                                                    data-date={reportDate}
                                                                    data-datefrom={view.report === 'periode' ? reportDateTimeStart : undefined}
                                                                    data-dateto={view.report === 'periode' ? reportDateTimeEnd : undefined}
                                                                    data-type={view.report}
                                                                    data-onlyfromdate={onlyFromDate}
                                                                    onClick={this.onClickUserDetail}
                                                                >
                                                                    <i class='fa-duotone fa-eye' /> <Text id='dyrejournal.report.sale-download'>By sales</Text>
                                                                </button>
                                                                <button
                                                                    class='btn btn-sm btn-outline-primary rounded-pill py-0 ms-2'
                                                                    data-id={userSale.user?.id}
                                                                    data-date={reportDate}
                                                                    data-datefrom={view.report === 'periode' ? reportDateTimeStart : undefined}
                                                                    data-dateto={view.report === 'periode' ? reportDateTimeEnd : undefined}
                                                                    data-type={view.report}
                                                                    data-onlyfromdate={onlyFromDate}
                                                                    onClick={this.onClickUserDetailProducts}
                                                                >
                                                                    <i class='fa-duotone fa-eye' /> <Text id='dyrejournal.report.products-download'>By product lines</Text>
                                                                </button>
                                                            </nobr>
                                                        </td>
                                                    </tr>
                                                </>);
                                            })}
                                        </tbody>
                                        {aggSalePersons && aggSalePersons.length > 0 && (() => {
                                            const t = aggSalePersons.reduce((acc, u) => {
                                                const hasOnlyPrice = !(u.fee || u.medicine || u.equipment || u.other);
                                                const totalSales = u.accounts?.reduce((a, i) => a + (i.saleCount || 0), 0) || 0;
                                                acc.salg += totalSales;
                                                acc.honorar += u.honorar || 0;
                                                acc.medicine += u.medicine || 0;
                                                acc.equipment += u.equipment || 0;
                                                acc.annet += hasOnlyPrice ? (u.price || 0) : (u.other || 0);
                                                acc.pris += hasOnlyPrice ? (u.price || 0) : ((u.fee || 0) + (u.medicine || 0) + (u.equipment || 0) + (u.other || 0));
                                                acc.rabatt += (u.discountAmount || 0) / 1.25;
                                                acc.commNet += u.commissionNet || 0;
                                                acc.commGross += u.commissionGross || 0;
                                                return acc;
                                            }, { salg: 0, honorar: 0, medicine: 0, equipment: 0, annet: 0, pris: 0, rabatt: 0, commNet: 0, commGross: 0 });
                                            return (
                                                <tfoot>
                                                    <tr class='fw-bold border-top'>
                                                        <td><Text id='dyrejournal.report.total'>Total</Text></td>
                                                        <td style='text-align: right;'><nobr>{util.format(t.salg, 0)}</nobr></td>
                                                        <td style='text-align: right;'><nobr>{util.format(t.honorar, 2)}</nobr></td>
                                                        <td style='text-align: right;'><nobr>{util.format(t.medicine, 2)}</nobr></td>
                                                        <td style='text-align: right;'><nobr>{util.format(t.equipment, 2)}</nobr></td>
                                                        <td style='text-align: right;'><nobr>{util.format(t.annet, 2)}</nobr></td>
                                                        <td style='text-align: right;'><nobr>{util.format(t.pris, 2)}</nobr></td>
                                                        <td style='text-align: right;'><nobr>{util.format(t.rabatt, 2)}</nobr></td>
                                                        <td style='text-align: right;'></td>
                                                        <td style='text-align: right;'><nobr>{util.format(t.commNet, 2)}</nobr></td>
                                                        <td style='text-align: right;'><nobr>{util.format(t.commGross, 2)}</nobr></td>
                                                        <td></td>
                                                    </tr>
                                                </tfoot>
                                            );
                                        })()}
                                    </table>
                                </div>
                                <div class='w-100 d-flex justify-content-center'>
                                    {extendedUser ? <>
                                        <button
                                            class='btn btn-sm btn-link text-muted p-0'
                                            onClick={this.toggleExtendedUser}
                                            >
                                            <Text id='gui.viewbox-less'>Show less</Text> <i class='fa-duotone fa-angle-up' />
                                        </button>
                                    </> : <>
                                        <button
                                            class='btn btn-sm btn-link text-muted p-0'
                                            onClick={this.toggleExtendedUser}
                                            >
                                            <Text id='gui.viewbox-more'>Show more</Text> <i class='fa-duotone fa-angle-down' />
                                        </button>
                                    </>}
                                </div>

                            </div>
                            <div class='w-100 d-flex flex-row justify-content-center'>
                                <small class='text-muted'>
                                    <Text id='dyrejournal.report.user-sales-help'>Sales by user. The sum is the total sale for this user without rounding price to nearest NOK. Tax is NOT included in the sum.</Text>
                                </small>
                            </div>
                            <div class='w-100 d-flex flex-row justify-content-center mt-1'>
                                <small class='text-body-secondary text-center'>
                                    <i class='fa-duotone fa-circle-info me-1' />
                                    <Text id='dyrejournal.report.user-sales-collective-help'>These totals include collectiveInvoice — work that is registered but not yet invoiced. The sum can therefore be higher than the Omsetning shown at the top of the report, which only counts invoiced sales.</Text>
                                </small>
                            </div>
                        </div>
                    </div>}

                    {view.report !== 'accounts-yearly' && view.report !== 'accounts-receivable' && <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.payment-user-sales'>Payments by user</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <div
                                    class='table-responsive'
                                    style={`
                                        max-height: ${extendedPaymentUser ? 'unset' : `${USER_HEIGHT}px;`};
                                    `}
                                >
                                    {(() => {
                                        const allPaymentMethods = [...new Set(
                                            (aggSalePaymentsByUser || []).flatMap(u => u.paymentMethods.map(pm => pm.paymentMethod))
                                        )].sort();
                                        const totals = {};
                                        allPaymentMethods.forEach(pm => { totals[pm] = { amount: 0, count: 0 }; });
                                        let grandTotal = 0;
                                        let grandCount = 0;
                                        (aggSalePaymentsByUser || []).forEach(u => {
                                            u.paymentMethods.forEach(pm => {
                                                totals[pm.paymentMethod].amount += pm.amount;
                                                totals[pm.paymentMethod].count += pm.count;
                                            });
                                            grandTotal += u.totalAmount;
                                            grandCount += u.totalCount;
                                        });
                                        return (
                                            <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em; line-height: 1.0em;'>
                                                <thead>
                                                    <tr>
                                                        <th class='w-25'><Text id='dyrejournal.report.user'>User</Text></th>
                                                        {allPaymentMethods.map(pm => (
                                                            <th style='text-align: right;' key={pm}>{saleStore.getPaymentMethodText(pm)}</th>
                                                        ))}
                                                        <th style='text-align: right;'><Text id='dyrejournal.report.total'>Total</Text></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {aggSalePaymentsByUser && aggSalePaymentsByUser.map((userPayment, idx) => (
                                                        <tr key={idx}>
                                                            <td>{userPayment.user?.firstname} {userPayment.user?.lastname}</td>
                                                            {allPaymentMethods.map(pm => {
                                                                const pmData = userPayment.paymentMethods.find(p => p.paymentMethod === pm);
                                                                return (
                                                                    <td style='text-align: right;' key={pm}>
                                                                        <nobr>
                                                                            {pmData ? <>
                                                                                {util.format(pmData.amount, 2)} <span class='font-weight-lighter'>({pmData.count})</span>
                                                                            </> : <></>}
                                                                        </nobr>
                                                                    </td>
                                                                );
                                                            })}
                                                            <td style='text-align: right;'>
                                                                <nobr>
                                                                    <strong>{util.format(userPayment.totalAmount, 2)}</strong> <span class='font-weight-lighter'>({userPayment.totalCount})</span>
                                                                </nobr>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {aggSalePaymentsByUser && aggSalePaymentsByUser.length > 0 && (
                                                        <tr class='fw-bold'>
                                                            <td><Text id='dyrejournal.report.total'>Total</Text></td>
                                                            {allPaymentMethods.map(pm => (
                                                                <td style='text-align: right;' key={pm}>
                                                                    <nobr>
                                                                        {util.format(totals[pm].amount, 2)} <span class='font-weight-lighter'>({totals[pm].count})</span>
                                                                    </nobr>
                                                                </td>
                                                            ))}
                                                            <td style='text-align: right;'>
                                                                <nobr>
                                                                    {util.format(grandTotal, 2)} <span class='font-weight-lighter'>({grandCount})</span>
                                                                </nobr>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        );
                                    })()}
                                </div>
                                <div class='w-100 d-flex justify-content-center'>
                                    {extendedPaymentUser ? <>
                                        <button
                                            class='btn btn-sm btn-link text-muted p-0'
                                            onClick={this.toggleExtendedPaymentUser}
                                            >
                                            <Text id='gui.viewbox-less'>Show less</Text> <i class='fa-duotone fa-angle-up' />
                                        </button>
                                    </> : <>
                                        <button
                                            class='btn btn-sm btn-link text-muted p-0'
                                            onClick={this.toggleExtendedPaymentUser}
                                            >
                                            <Text id='gui.viewbox-more'>Show more</Text> <i class='fa-duotone fa-angle-down' />
                                        </button>
                                    </>}
                                </div>

                            </div>
                            <div class='w-100 d-flex flex-row justify-content-center'>
                                <small class='text-muted'>
                                    <Text id='dyrejournal.report.payment-user-sales-help'>Payments received by user broken down by payment method. Shows who received each payment and the method used.</Text>
                                </small>
                            </div>
                        </div>
                    </div>}

                    {view.report !== 'accounts-yearly' && view.report !== 'accounts-receivable' && <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.account-sales'>Sales by account</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <div class='table-responsive w-100'>

                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em; line-height: 1.0em;'>
                                        <thead>
                                            <tr>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.sales'>Sales</Text></th>
                                                <th><Text id='dyrejournal.report-account'>Konto</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.sum'>Sum</Text></th>
                                                {/* <th style='text-align: right;'><Text id='dyrejournal.report.tax'>Tax inc</Text></th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {aggSaleTotalAccounts && aggSaleTotalAccounts.map(account => {
                                                const accountKey = String(account.account || 'none');
                                                const isExpanded = expandedAccountRows[accountKey];
                                                const sortedSaleIds = account.saleIds ? [...account.saleIds].sort((a, b) => a - b) : [];
                                                return <>
                                                    <tr onClick={() => this.toggleAccountRow(accountKey)} style='cursor: pointer;'>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                <span style={`display: inline-block; width: 14px; font-size: 0.8em; transition: transform 0.2s; transform: rotate(${isExpanded ? '90deg' : '0deg'});`}>&#9654;</span>
                                                                {util.format(account.saleCount, 0)}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: left;'>
                                                            <nobr>
                                                                {account.account || ''} {getAccountName(account.account, userStore.customer) || (!account.account ? 'Ingen konto' : '')}
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(account.price, 2)}
                                                            </nobr>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && sortedSaleIds.length > 0 && <tr>
                                                        <td colSpan='3' style='padding: 4px 8px 8px 30px;'>
                                                            <div style='display: flex; flex-wrap: wrap; gap: 4px; max-height: 200px; overflow-y: auto;'>
                                                                {sortedSaleIds.map(saleId => (
                                                                    <a href={`/dyrejournal-sale/${saleId}`}
                                                                        class='badge bg-secondary text-decoration-none'
                                                                        onClick={e => this.onClickGotoSale(e, saleId)}>
                                                                        {saleId}
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>}
                                                </>;
                                            })}
                                            <tr>
                                                <td class='font-weight-bold' style='text-align: right;'>{aggSaleTotalAccounts.reduce((acc, item) => acc + item.saleCount, 0)}</td>
                                                <td class='font-weight-bold'><Text id='dyrejournal.report.total-ex-tax'>Total without tax</Text></td>
                                                <td class='font-weight-bold' style='text-align: right;'>{util.format(totalForAccounts, 2)}</td>
                                            </tr>
                                            <tr>
                                                <td>&nbsp;</td>
                                                <td class='font-weight-bold'><Text id='dyrejournal.report.total-inc-tax'>Total with tax</Text></td>
                                                <td class='font-weight-bold' style='text-align: right;'>{util.format(totalForAccountsIncVat, 2)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div class='w-100 d-flex flex-row justify-content-center px-3'>
                                <small class='text-muted'>
                                    <Text id='dyrejournal.report.account-sales-help'>Sales by account. The sum is the total sale for this account without rounding price to nearest NOK. Tax is NOT included in the sum.</Text>
                                </small>
                            </div>
                        </div>

                        <div class='w-100 d-flex flex-column'>
                            <div class='w-100 d-flex flex-column'>
                                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                    <Text id='dyrejournal.report.journal-sales'>Sales with journals</Text>
                                </div>
                                <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                    <div class='table-responsive w-100'>

                                        <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em; line-height: 1.0em;'>
                                            <thead>
                                                <tr>
                                                    <th style='text-align: right;'><Text id='dyrejournal.report.sales'>Sales</Text></th>
                                                    <th style='text-align: right;'><Text id='dyrejournal.report.sum'>Sum</Text></th>
                                                    {/* <th style='text-align: right;'><Text id='dyrejournal.report.tax'>Tax inc</Text></th> */}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {aggSaleWithJournals && aggSaleWithJournals.saleCount && <>
                                                    <tr>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(aggSaleWithJournals.saleCount, 0)}
                                                                <span class='text-muted ms-1'>
                                                                    (<Text id='dyrejournal.report.of'>of</Text> {aggSaleTotal.saleCount ? `${util.format(aggSaleTotal.saleCount, 0)}` : ''})
                                                                </span>
                                                            </nobr>
                                                        </td>
                                                        <td style='text-align: right;'>
                                                            <nobr>
                                                                {util.format(aggSaleWithJournals.price, 2)}
                                                            </nobr>
                                                        </td>
                                                    </tr>
                                                </>}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div class='w-100 d-flex flex-row justify-content-center px-3'>
                                    <small class='text-muted'>
                                        <Text id='dyrejournal.report.journal-sales-help'>Sales with journals. The sum is the total sale without rounding price to nearest NOK. Tax is NOT included in the sum.</Text>
                                    </small>
                                </div>
                            </div>
                            <div class='w-100 d-flex flex-column mt-3'>
                                <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                    <Text id='dyrejournal.report.journal-count'>Total journals</Text>
                                </div>
                                <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                    <div class='table-responsive w-100'>

                                        <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em; line-height: 1.0em;'>
                                            <thead>
                                                <tr>
                                                    <th style='text-align: left;'><Text id='dyrejournal.report.type'>Type</Text></th>
                                                    <th style='text-align: right;'><Text id='dyrejournal.report.journal'>Journals</Text></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td style='text-align: left;'>
                                                        <nobr>
                                                            <Text id='dyrejournal.report.total-journals'>Total journals</Text>
                                                        </nobr>
                                                    </td>
                                                    <td style='text-align: right;'>
                                                        <nobr>
                                                            {util.format(journalCount, 0)}
                                                        </nobr>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style='text-align: left;'>
                                                        <Text id='dyrejournal.report.journals-without-complaint'>Journals without chiefComplaint containing "ormekur" and "kloklipp".</Text>
                                                    </td>
                                                    <td style='text-align: right;'>
                                                        <nobr>
                                                            {util.format(journalWithoutCount, 0)}
                                                        </nobr>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div class='w-100 d-flex flex-row justify-content-center px-3'>
                                    <small class='text-muted'>
                                        <Text id='dyrejournal.report.journal-total-help'>Total journals for this periode.</Text>
                                    </small>
                                </div>
                            </div>
                        </div>



                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.visitors'>Visitors</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <div class='table-responsive w-100'>

                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em; line-height: 1.0em;'>
                                        <thead>
                                            <tr>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.type'>Type</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.sum'>Sum</Text></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style='text-align: right;'><Text id='dyrejournal.report.new-visitors'>New visitors</Text></td>
                                                <td class='text-end'>{aggSaleNewVisitors}</td>
                                            </tr>
                                            <tr>
                                                <td style='text-align: right;'><Text id='dyrejournal.report.active-visitors'>Active visitors</Text></td>
                                                <td class='text-end'>{aggSaleActiveVisitors}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div class='w-100 d-flex flex-row justify-content-center px-3'>
                                <small class='text-muted'>
                                    <Text id='dyrejournal.report.journal-newvisitors-help'>Visitors statistics for this periode.</Text>
                                </small>
                            </div>
                        </div>
                    </div>}


                    {view.report !== 'accounts-yearly' && view.report !== 'accounts-receivable' && <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.products-sales'>Sales by products</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <div
                                    class='table-responsive w-100'
                                    style={`
                                        max-height: ${extendedProduct ? 'unset' : `${PRODUCT_HEIGHT}px;`};
                                    `}
                                    ref={el => this.productsContainer = el}
                                >

                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.9em; line-height: 1.0em;'>
                                        <thead>
                                            <tr>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.sales'>Sales</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.qty'>Qty</Text></th>
                                                <th><Text id='dyrejournal.report-product'>Product</Text></th>
                                                <th style='text-align: right;'><Text id='dyrejournal.report.sum'>Sum</Text></th>
                                                {/* <th style='text-align: right;'><Text id='dyrejournal.report.tax'>Tax inc</Text></th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {aggSaleTotalProducts && aggSaleTotalProducts.map(product => <>
                                                <tr>
                                                    <td style='text-align: right;'>
                                                        <nobr>
                                                            {util.format(product.saleCount, 0)}
                                                        </nobr>
                                                    </td>
                                                    <td style='text-align: right;'>
                                                        <nobr>
                                                            {util.format(product.count, 0)}
                                                        </nobr>
                                                    </td>
                                                    <td style='text-align: left;'>
                                                        <nobr>
                                                            {product.productNumber}: {product.productName}
                                                        </nobr>
                                                    </td>

                                                    <td style='text-align: right;'>
                                                        <nobr>
                                                            {util.format(product.price, 2)}
                                                        </nobr>
                                                    </td>
                                                    {/* <td style='text-align: right;'>
                                                        <nobr>
                                                            {util.format(account.price / 1.25 * 0.25, 2)}
                                                        </nobr>
                                                    </td> */}
                                                </tr>
                                            </>)}
                                            {/* <tr>
                                                <td class='font-weight-bold' style='text-align: right;'>{aggSaleTotalProducts.reduce((acc, item) => acc + item.saleCount, 0)}</td>
                                                <td class='font-weight-bold'><Text id='dyrejournal.report.total-ex-tax'>Total without tax</Text></td>
                                                <td class='font-weight-bold' style='text-align: right;'>{util.format(totalForAccounts, 2)}</td>
                                            </tr> */}
                                            {/* <tr>
                                                <td>&nbsp;</td>
                                                <td class='font-weight-bold'><Text id='dyrejournal.report.total-inc-tax'>Total with tax</Text></td>
                                                <td class='font-weight-bold' style='text-align: right;'>{util.format(totalForAccounts * 1.25, 2)}</td>
                                            </tr> */}
                                        </tbody>
                                    </table>
                                </div>

                                <div class='w-100 d-flex justify-content-center'>
                                    {extendedProduct ? <>
                                        <button
                                            class='btn btn-sm btn-link text-muted p-0'
                                            onClick={this.toggleExtendedProduct}
                                            >
                                            <Text id='gui.viewbox-less'>Show less</Text> <i class='fa-duotone fa-angle-up' />
                                        </button>
                                    </> : <>
                                        <button
                                            class='btn btn-sm btn-link text-muted p-0'
                                            onClick={this.toggleExtendedProduct}
                                            >
                                            <Text id='gui.viewbox-more'>Show more</Text> <i class='fa-duotone fa-angle-down' />
                                        </button>
                                    </>}
                                </div>

                            </div>
                            <div class='w-100 d-flex flex-row justify-content-center px-3'>
                                <small class='text-muted'>
                                    <Text id='dyrejournal.report.account-sales-help'>Sales by account. The sum is the total sale for this account without rounding price to nearest NOK. Tax is NOT included in the sum.</Text>
                                </small>
                            </div>
                        </div>
                    </div>}


                    {view.report !== 'accounts-yearly' && view.report !== 'accounts-receivable' && <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.list'>Report list</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                <div
                                    class='table-responsive'
                                    style={`
                                        max-height: ${extendedReportlist ? 'unset' : `${REPORTLIST_HEIGHT}px;`};
                                    `}
                                    ref={el => this.reportlistContainer = el}

                                >

                                    <table class='table table-sm table-hover table-striped font-style-narrow' style='font-size: 0.7em; line-height: 1.0em;'>
                                        <thead>
                                            <tr>
                                                <th><nobr>Dato</nobr></th>
                                                <th><nobr>Laget</nobr></th>
                                                <th><nobr>Type</nobr></th>
                                                <th class='text-end'><nobr>Telt</nobr></th>
                                                <th class='text-end'><nobr>+Veksel</nobr></th>
                                                <th class='text-end'><nobr>-Bank</nobr></th>
                                                <th class='text-end'><nobr>Kontant</nobr></th>
                                                <th class='text-end'><nobr>Kort</nobr></th>
                                                {showCreditCard && <th class='text-end'><nobr>Kredkort</nobr></th>}
                                                <th class='text-end'><nobr>Vipps</nobr></th>
                                                <th class='text-end'><nobr>Faktura</nobr></th>
                                                <th class='text-end'><nobr>Kredit</nobr></th>
                                                <th class='text-end'><nobr>Konto</nobr></th>
                                                <th class='text-end'><nobr>Delbet</nobr></th>
                                                <th class='text-end'><nobr>Kredit.</nobr></th>
                                                <th class='text-end'><nobr>Omsetn.</nobr></th>
                                                <th class='text-end'><nobr>Rabatt</nobr></th>
                                                <th class='text-end'><nobr>Mva</nobr></th>
                                                <th>&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {saleReports && saleReports.map((item, idx) => {
                                                return (<>
                                                    <tr>
                                                        <td>
                                                            {item.reportType === 'periode' ? <>
                                                                <nobr>{util.isoDate(item.reportDateTimeStart, false, false, true)}</nobr>
                                                            </> : <>
                                                                <nobr>{util.isoDate(item.reportDate, false, false, true)}</nobr>
                                                            </>}
                                                        </td>
                                                        <td>
                                                            <nobr class='text-body-secondary'>{item.createdDate ? util.isoDate(item.createdDate) : ''}</nobr>
                                                        </td>
                                                        <td>
                                                            {item.reportType === 'day' && <nobr>Dag</nobr>}
                                                            {item.reportType === 'periode' && <nobr>Per</nobr>}
                                                            {item.reportType === 'z-report' && <nobr>Z</nobr>}
                                                            {item.reportType === 'month' && <nobr>Mnd</nobr>}
                                                            {item.reportType === 'year' && <nobr>År</nobr>}
                                                        </td>
                                                        <td class='text-end'><nobr>{util.format(item.countedCash, 0)}</nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.addedChange || 0, 0)}</nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.bankWithdrawal || 0, 0)}</nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.amountCash, 0)}</nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.amountCard, 0)}</nobr></td>
                                                        {showCreditCard && <td class='text-end'><nobr>{util.format(item.amountCreditCard, 0)}</nobr></td>}
                                                        <td class='text-end'><nobr>{util.format(item.amountVipps, 0)}</nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.amountInvoice, 0)}</nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.amountCreditNote, 0)}</nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.amountAccount, 0)}</nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.amountPartial, 0)}</nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.amountSaleCredited, 0)}</nobr></td>
                                                        <td class='text-end'><nobr><strong>{util.format(item.amountTotal, 0)}</strong></nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.amountDiscount, 0)}</nobr></td>
                                                        <td class='text-end'><nobr>{util.format(item.amountTax, 0)}</nobr></td>
                                                        <td class='text-end'>
                                                            <nobr>
                                                                <button type='button' class='btn btn-sm btn-link p-0 me-1' onClick={this.onClickPrintReport} data-id={item.id} title='Print'>
                                                                    <i class='fa-solid fa-print' />
                                                                </button>
                                                                <a
                                                                    class='btn btn-sm btn-link p-0 me-1'
                                                                    href={`${apiServer}/api/salereports/download/csv?jwtToken=${jwtToken}&id=${item.id}`}
                                                                    target='_blank'
                                                                    rel='noopener noreferrer'
                                                                    title='Download'
                                                                >
                                                                    <i class='fa-solid fa-download' />
                                                                </a>
                                                                <button type='button' class='btn btn-sm btn-link p-0 text-danger' onClick={this.onClickDeleteSaleReport} data-id={item.id} title='Delete'>
                                                                    <i class='fa-solid fa-trash' />
                                                                </button>
                                                            </nobr>
                                                        </td>
                                                    </tr>
                                                </>);
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div class='w-100 d-flex justify-content-center'>
                                    {extendedReportlist ? <>
                                        <button
                                            class='btn btn-sm btn-link text-muted p-0'
                                            onClick={this.toggleExtendedReportlist}
                                            >
                                            <Text id='gui.viewbox-less'>Show less</Text> <i class='fa-duotone fa-angle-up' />
                                        </button>
                                    </> : <>
                                        <button
                                            class='btn btn-sm btn-link text-muted p-0'
                                            onClick={this.toggleExtendedReportlist}
                                            >
                                            <Text id='gui.viewbox-more'>Show more</Text> <i class='fa-duotone fa-angle-down' />
                                        </button>
                                    </>}
                                </div>

                                <div class='w-100 d-flex flex-row justify-content-center'>
                                    <button
                                        type='button'
                                        class={`btn btn-secondary rounded-pill mx-2`}
                                        onClick={this.onClickUpdateList}
                                        disabled={saveReportUpdating}
                                    >
                                        <i class='fa-solid fa-sync' /> <Text id='dyrejournal.report.update-list'>Update list</Text>
                                        {saveReportUpdating && <i class='fa-solid fa-spinner-third fa-spin' />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>}

                    {/* <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.appointments'>Appointments</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                Report for today's appointments
                            </div>
                        </div>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.prescriptions'>Prescriptions</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                Report for today's prescriptions
                            </div>
                        </div>
                    </div> */}

                    {/* <div class='w-100 d-flex flex-sm-row flex-column mt-3'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.invoices'>Invoices</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                Report for today's invoices
                            </div>
                        </div>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.report.appointments'>Drugs</Text>
                            </div>
                            <div class={`d-flex flex-column mx-3 px-3 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                Report for today's drugs
                            </div>
                        </div>
                    </div> */}

                </div>
            </div>
        );
    }
}

export default AllProducts;
